import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import Toolbar from '../components/ToolBar';
import _ from 'lodash';
import ExecutionLogs from '../components/ExecutionLogs';
import { useKeyPress } from 'reactflow';
// Import auth context
import { useAuth } from '../contexts/AuthContext';
import NotificationModal from '../components/NotificationModal';



// Import the custom nodes
import AvailableColumns from '../components/AvailableColumns';
import {
  UploadDatasetNode,
  ConfigureFeaturesNode,
  ImputeNode,
  ScaleNode,
  EncodeNode,
  SelectFeaturesNode,
  ReduceDimensionsNode,
  TrainModelNode,
  TrainCNNNode
  
} from '../components/CustomNodes';
import DatasetSelector from '../components/DatasetSelector';
import api from '../services/api';    // Your backend service for pipeline storage (if used)
import mlApi from '../services/mlApi'; // The ML microservice for /upload, /features/configure, /preprocess, /train
import { Train } from 'lucide-react';

import AutoMLSuggestion from '../components/AutoMLSuggestion'; // Add this import
import ProblemTypeVisualizer from '../components/ProblemTypeVisualizer';

import HyperparameterOptimizer from '../components/HyperparameterOptimizer';
import ModelExplanations from '../components/ModelExplanations';


////////////////////////////////////////////////////////////////////////////////
// 1. Node Definitions
//    Each node type corresponds to your actual endpoints, with parameter fields.
////////////////////////////////////////////////////////////////////////////////
const NODE_CATEGORIES = [
  {
    category: 'Data',
    nodes: [
      {
        type: 'uploadDataset',
        label: 'Upload Dataset',
        icon: '📁',
        defaultParams: {
          file: null,
        },
      },
    ],
  },
  {
    category: 'Feature Config',
    nodes: [
      {
        type: 'configureFeatures',
        label: 'Configure Features',
        icon: '⚙️',
        defaultParams: {
          feature_columns: [],
          target_column: '',
        },
      },
    ],
  },
  {
    category: 'Data Processing',
    nodes: [
      {
        type: 'scale',
        label: 'Scale Features',
        icon: '📊',
        defaultParams: {
          method: 'standard',
          feature_range_min: 0.0,
          feature_range_max: 1.0
        },
      },
      {
        type: 'encode',
        label: 'Encode Categories',
        icon: '🏷️',
        defaultParams: {
          method: 'onehot',
          handle_unknown: 'ignore',
          ordinal_categories: {}
        },
      },
      {
        type: 'selectFeatures',
        label: 'Feature Selection',
        icon: '🎯',
        defaultParams: {
          method: 'variance',
          threshold: 0.0,
          k: 10,
          percentile: 75
        },
      },
      {
        type: 'reduceDimensions',
        label: 'Dimensionality Reduction',
        icon: '📉',
        defaultParams: {
          method: 'pca',
          n_components: 2,
          random_state: 42
        },
      },
      {
        type: 'impute',
        label: 'Impute missing values',
        icon: '🔧',
        defaultParams: {
          numeric_impute_strategy: 'mean',
          categorical_impute_strategy: 'most_frequent',
        },
      },
    ],
  },
  {
    category: 'Model Training',
    nodes: [
      {
        type: 'trainClassification',
        label: 'Classification Models',
        icon: '🎯',
        defaultParams: {
          model_type: 'random_forest',
          test_size: 0.2,
          random_state: 42,
          
          // Tree-based models
          n_estimators: 100,
          max_depth: 10,
          criterion: 'gini',
          
          // Linear models
          fit_intercept: true,
          C: 1.0,
          
          // SVM models
          kernel: 'rbf',
          
          // Gradient Boosting
          learning_rate: 0.1,
          max_iter: 1000
        },
      },
      {
        type: 'trainRegression',
        label: 'Regression Models',
        icon: '📈',
        defaultParams: {
          model_type: 'linear_regression',
          test_size: 0.2,
          random_state: 42,
          
          // Linear models
          fit_intercept: true,
          alpha: 1.0,
          l1_ratio: 0.5,
          
          // Tree-based models
          n_estimators: 100,
          max_depth: 10,
          
          // SVM models
          kernel: 'rbf',
          C: 1.0,
          epsilon: 0.1,
          
          // Gradient Boosting
          learning_rate: 0.1,
          max_iter: 1000
        },
      },
      {
        type: 'trainTimeSeries',
        label: 'Time Series Models',
        icon: '📊',
        defaultParams: {
          model_type: 'arima',
          test_size: 0.2,
          
          // ARIMA parameters
          p: 1,
          d: 1,
          q: 1,
          
          // SARIMA parameters
          seasonal_p: 1,
          seasonal_d: 1,
          seasonal_q: 1,
          seasonal_periods: 12,
          
          // Prophet parameters
          yearly_seasonality: true,
          weekly_seasonality: true,
          daily_seasonality: false,
          growth: 'linear',
          forecast_periods: 30
        },
      },
      {
        type: 'trainDeepLearning',
        label: 'Deep Learning Models',
        icon: '🧠',
        defaultParams: {
          model_type: 'mlp',
          test_size: 0.2,
          random_state: 42,
          
          // Neural Network parameters
          hidden_layers: [100, 50],
          activation: 'relu',
          epochs: 100,
          learning_rate: 0.001,
          dropout_rate: 0.2,
          
          // LSTM parameters
          lstm_units: [50, 50],
          sequence_length: 10
        },
      },
      {
        type: 'trainCNN',
        label: 'Computer Vision (CNN)',
        icon: '🖼️',
        defaultParams: {
          architecture: 'resnet50',
          img_size: 224,
          batch_size: 32,
          epochs: 10,
          learning_rate: 0.001,
          fine_tune: true,
          augmentation: true,
          test_size: 0.2,
        },
      },
      {
        type: 'trainClustering',
        label: 'Clustering Models',
        icon: '🎪',
        defaultParams: {
          model_type: 'kmeans',
          
          // K-means parameters
          n_clusters: 3,
          kmeans_init: 'k-means++',
          random_state: 42,
          
          // DBSCAN parameters
          eps: 0.5,
          min_samples: 5,
          
          // Hierarchical clustering
          linkage: 'ward'
        },
      },
    ],
  },
];

// If you have custom node components, define them here. For now, we'll use defaults.
// Update the nodeTypes definition (place this inside your component)
const nodeTypes = {
  uploadDataset: UploadDatasetNode,
  configureFeatures: ConfigureFeaturesNode,
  impute: ImputeNode,
  scale: ScaleNode,
  encode: EncodeNode,
  selectFeatures: SelectFeaturesNode,
  reduceDimensions: ReduceDimensionsNode,
  trainClassification: TrainModelNode,
  trainRegression: TrainModelNode,
  trainTimeSeries: TrainModelNode,
  trainDeepLearning: TrainModelNode,
  trainCNN: TrainCNNNode,
  trainClustering: TrainModelNode
};

const MODEL_TYPE_TO_NODE = {
  random_forest: 'trainClassification',
  decision_tree: 'trainClassification',
  svc: 'trainClassification',
  logistic_regression: 'trainClassification',
  gradient_boosting: 'trainClassification',
  xgboost: 'trainClassification',
  knn: 'trainClassification',
  naive_bayes: 'trainClassification',
  linear_regression: 'trainRegression',
  ridge: 'trainRegression',
  lasso: 'trainRegression',
  svr: 'trainRegression',
  elastic_net: 'trainRegression',
  random_forest_regressor: 'trainRegression',
  gradient_boosting_regressor: 'trainRegression',
  mlp: 'trainDeepLearning',
  lstm: 'trainDeepLearning',
  arima: 'trainTimeSeries',
  sarima: 'trainTimeSeries',
  prophet: 'trainTimeSeries',
  kmeans: 'trainClustering',
  dbscan: 'trainClustering',
  hierarchical: 'trainClustering',
  resnet50: 'trainCNN',
  vgg16: 'trainCNN',
};

////////////////////////////////////////////////////////////////////////////////
// 2. PipelineEditor
////////////////////////////////////////////////////////////////////////////////
export default function PipelineEditor() {
  const { pipelineId } = useParams();
  const { user } = useAuth();

  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Optionally store pipeline from your own backend
  const [pipeline, setPipeline] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isModelSaving, setIsModelSaving] = useState(false);
  const [modelSaveSuccess, setModelSaveSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('nodes');
  const [notification, setNotification] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [isLogsOpen, setIsLogsOpen] = useState(false);


  // React Flow instance & reference
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Logs (on the right panel)
  const [logs, setLogs] = useState([]);

  // “Add Node” panel
  const [showAddNodePanel, setShowAddNodePanel] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Node config modal (for both creating new nodes & editing existing ones)
  const [showNodeConfigModal, setShowNodeConfigModal] = useState(false);
  // The node definition (type, label, etc.) or existing node we're editing
  const [modalNodeDef, setModalNodeDef] = useState(null);
  // The actual parameters we're editing in the modal
  const [modalParams, setModalParams] = useState({});

  // If the user clicks a node that already exists, we store its ID to update after saving
  const [editNodeId, setEditNodeId] = useState(null);

  // The datasetId from /upload (if used). We'll store here so subsequent calls can use it.
  const [datasetId, setDatasetId] = useState(null);
  const [trainedModelId, setTrainedModelId] = useState(null);

  const [activeTabOnOpen, setActiveTabOnOpen] = useState('logs');

  const deleteKeyPressed = useKeyPress('Delete');
  const backspaceKeyPressed = useKeyPress('Backspace');

  // Add these state variables at the top of your PipelineEditor component
  const [showAutoML, setShowAutoML] = useState(false);
  const [datasetAnalysis, setDatasetAnalysis] = useState(null);

  const [detectedProblemType, setDetectedProblemType] = useState(null);
const [overrideProblemType, setOverrideProblemType] = useState(false);
const [manualProblemType, setManualProblemType] = useState('classification');
const [showProblemTypeHelp, setShowProblemTypeHelp] = useState(false);

// Add these state variables to your PipelineEditor component
const [enableOptimization, setEnableOptimization] = useState(false);
const [optimizationConfig, setOptimizationConfig] = useState({
  method: 'random',
  cv_folds: 5,
  n_iterations: 50,
  scoring: 'accuracy'
});
const [enableExplanations, setEnableExplanations] = useState(false);
const [modelExplanations, setModelExplanations] = useState(null);

const [showExplanationsModal, setShowExplanationsModal] = useState(false);
  const [currentExplanations, setCurrentExplanations] = useState(null);
  const [currentModelType, setCurrentModelType] = useState('');


  useEffect(() => {
    if (deleteKeyPressed || backspaceKeyPressed) {
      // Get selected nodes and delete them
      const selectedNodes = nodes.filter(node => node.selected);
      if (selectedNodes.length > 0) {
        setNodes(nodes.filter(node => !node.selected));
        
        // Also remove any connected edges
        const nodeIds = selectedNodes.map(node => node.id);
        setEdges(edges.filter(edge => 
          !nodeIds.includes(edge.source) && !nodeIds.includes(edge.target)
        ));
        
        // setLogs(prev => [...prev, `Deleted ${selectedNodes.length} node(s)`]);
      } else {
        // If no nodes selected, delete selected edges
        const selectedEdges = edges.filter(edge => edge.selected);
        if (selectedEdges.length > 0) {
          setEdges(edges.filter(edge => !edge.selected));
          setLogs(prev => [...prev, `Deleted ${selectedEdges.length} edge(s)`]);
        }
      }
    }
  }, [deleteKeyPressed, backspaceKeyPressed, nodes, edges, setNodes, setEdges]);

  const handleOpenDataLogs = useCallback(() => {
    setIsLogsOpen(true);
    // The ExecutionLogs component needs to know which tab to show
    setActiveTabOnOpen('data');
  }, []);

  // If the user presses delete or backspace, remove selected nodes/edges

  // Add this function inside the PipelineEditor component
const handleDeleteNode = useCallback((nodeId) => {
  setNodes((nds) => nds.filter((node) => node.id !== nodeId));
  
  // Also remove any connected edges
  setEdges((eds) => 
    eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
  );
  
  // setLogs(prev => [...prev, `Deleted node`]);
}, [setNodes, setEdges, setLogs]);

  /////////////////////////////////////////////////////////////////////////////
  // 2.1: FETCH PIPELINE (If you're using a backend)
  /////////////////////////////////////////////////////////////////////////////
  useEffect(() => {
    if (pipelineId) {
      fetchPipeline();
    }
  }, [pipelineId]);

  // Updated fetch function to reconstruct edges from connections
 // Updated fetch function
// Updated fetch function
const fetchPipeline = async () => {
    try {
      const response = await api.get(`/pipelines/${pipelineId}`);
      setPipeline(response.data);
      
      if (response.data?.elements && Array.isArray(response.data.elements)) {
        // Separate nodes and edges
        const loadedNodes = response.data.elements
          .filter(el => el.type && !el.source && !el.target) // Only elements that are nodes (have type but no source/target)
          .map(node => ({
            id: node.id,
            type: node.type,
            position: node.position || { x: 0, y: 0 },
            data: {
              label: node.data?.label || '',
              parameters: node.data?.parameters || {},
              status: node.data?.status || 'pending',
              results: node.data?.results || null,
              onDelete: handleDeleteNode
            }
          }));
  
        const loadedEdges = response.data.elements
        .filter(el => el.source && el.target) // Only elements that are edges (have source and target)
          .map(edge => ({
            id: edge.id,
            source: edge.source,
            target: edge.target,
            type: edge.type || 'default'
          }));
          
           console.log('Loaded nodes:', loadedNodes);
          console.log('Loaded edges:', loadedEdges);

        setNodes(loadedNodes);
        setEdges(loadedEdges);
      } 
    } catch (error) {
      console.error('Error fetching pipeline:', error);
      setNodes([]);
      setEdges([]);
    }
  };
  


  /////////////////////////////////////////////////////////////////////////////
  // 2.2: REACT FLOW EVENTS
  /////////////////////////////////////////////////////////////////////////////
  // Function to transform node data for saving
  // Function to prepare node data with connections
  // Validation helper functions
const isValidNodeData = (node) => {
    if (!node) {
      console.log('Node is null or undefined');
      return false;
    }
    if (!node.id || !node.type) {
      console.log('Node missing required fields:', node);
      return false;
    }
    if (!node.position || typeof node.position.x !== 'number' || typeof node.position.y !== 'number') {
      console.log('Invalid position:', node.position);
      return false;
    }
    if (!node.data) {
      console.log('Node missing data:', node);
      return false;
    }
    return true;
  };
  
  // Function to prepare a node for saving
const prepareNodeForSave = (node) => ({
    id: node.id,
    type: node.type,
    position: {
      x: node.position?.x || 0,
      y: node.position?.y || 0
    },
    data: {
      label: node.data?.label || '',
      parameters: node.data?.parameters || {},
      status: node.data?.status || 'pending',
      results: node.data?.results || null,
      onDelete: handleDeleteNode
    }
  });
  
  // Function to prepare an edge for saving
  const prepareEdgeForSave = (edge) => ({
    id: edge.id,
    type: 'default',
    source: edge.source,
    target: edge.target
  });
  // Function to prepare edges and add them to nodes' connections
const addEdgesToNodes = (nodes, edges) => {
    // Create a copy of nodes
    const nodesWithConnections = [...nodes];
    
    // Add edge information to nodes' connections arrays
    edges.forEach(edge => {
      const sourceNode = nodesWithConnections.find(n => n.id === edge.source);
      if (sourceNode) {
        if (!sourceNode.connections) {
          sourceNode.connections = [];
        }
        sourceNode.connections.push({
          sourceId: edge.source,
          targetId: edge.target
        });
      }
    });
  
    return nodesWithConnections;
  };

  // Updated save function with better error handling
// Updated save function
const debouncedSave = useCallback(
    _.debounce(async (currentNodes, currentEdges) => {
      if (!pipelineId) return;
      
      setIsSaving(true);
      setSaveError(null);
      
      try {
        // Ensure we have arrays
        const nodes = Array.isArray(currentNodes) ? currentNodes : [];
        const edges = Array.isArray(currentEdges) ? currentEdges : [];
        
        // Prepare elements array
        const elements = [
          ...nodes.map(prepareNodeForSave),
          ...edges.map(prepareEdgeForSave)
        ];
        
        
        const response = await api.put(`/pipelines/${pipelineId}/elements`, {
          elements
        });
        
        setSaveError(null);
      } catch (error) {
        console.error('Error saving pipeline elements:', error);
        setSaveError(error.response?.data?.message || 'Error saving pipeline');
      } finally {
        setIsSaving(false);
      }
    }, 1000),
    [pipelineId]
  );
  
  
  

  // Update the connect handler to ensure edge IDs are unique
const onConnect = useCallback((params) => {
    setEdges((eds) => {
      const newEdge = {
        ...params,
        id: `edge-${params.source}-${params.target}-${Date.now()}`
      };
      return [...eds, newEdge];
    });
  }, [setEdges]);

  // Add a manual save function
const handleManualSave = async () => {
    if (!pipelineId) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await api.put(`/pipelines/${pipelineId}/elements`, {
        elements: [...nodes, ...edges]
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving pipeline elements:', error);
      setSaveError(error.response?.data?.message || 'Error saving pipeline');
    } finally {
      setIsSaving(false);
    }
  };

  // We do not drop from the Add Node panel. We spawn via a modal “Save” step.
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);
  const onDrop = useCallback(() => {}, []);

  // If a user clicks an existing node => open config modal for editing
  const onNodeClick = (event, node) => {
    setEditNodeId(node.id);
    setModalNodeDef({ type: node.type, label: node.data.label });
    setModalParams({ ...node.data.parameters });
    setShowNodeConfigModal(true);
  };

  /////////////////////////////////////////////////////////////////////////////
  // 2.3: “ADD NODE” PANEL
  /////////////////////////////////////////////////////////////////////////////
  const handleOpenAddNodePanel = () => setShowAddNodePanel(true);
  const handleCloseAddNodePanel = () => setShowAddNodePanel(false);

  const filteredCategories = NODE_CATEGORIES.map((cat) => ({
    ...cat,
    nodes: cat.nodes.filter((n) => {
      const lowerSearch = searchTerm.toLowerCase();
      return (
        n.label.toLowerCase().includes(lowerSearch) ||
        n.type.toLowerCase().includes(lowerSearch)
      );
    }),
  }));

  // User clicks on a node type => open the config modal (to set parameters before creating)
  const handleNodeDefSelect = (nodeDef) => {
    setEditNodeId(null); // we're creating a new node, not editing existing
    setModalNodeDef(nodeDef);
    setModalParams({ ...nodeDef.defaultParams });
    setShowNodeConfigModal(true);
    handleCloseAddNodePanel();
  };

  /////////////////////////////////////////////////////////////////////////////
  // 2.4: NODE CONFIG MODAL
  /////////////////////////////////////////////////////////////////////////////
  const handleCloseNodeConfigModal = () => {
    setShowNodeConfigModal(false);
    setModalNodeDef(null);
    setModalParams({});
    setEditNodeId(null);
  };

  // Called when user changes any parameter in the modal
  const handleParamChange = (paramName, value) => {
    
    setModalParams((prev) => ({ ...prev, [paramName]: value }));
  };

  // For uploadDataset file param
  const handleParamFileChange = (e) => {
    const fileObj = e.target.files[0] || null;
    handleParamChange('file', fileObj);
  };

  // Called when user clicks “Save Node” in the modal
  const handleSaveNodeConfig = () => {
    if (!reactFlowInstance || !reactFlowWrapper.current) {
      handleCloseNodeConfigModal();
      return;
    }

    // If we're editing an existing node, just update it in place
    if (editNodeId) {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === editNodeId) {
            return {
              ...node,
              data: {
                label: modalNodeDef.label,
              parameters: modalParams,
              status: 'pending',
              results: null,
              onDelete: handleDeleteNode
              },
            };
          }
          return node;
        })
      );
      handleCloseNodeConfigModal();
      return;
    }

    // Otherwise, create a brand new node at some position (e.g. x=100, y=100)
    const position = reactFlowInstance.project({ x: 100, y: 100 });
  const newNode = {
    id: `${modalNodeDef.type}-${Date.now()}`,
    type: modalNodeDef.type,
    position,
    data: {
      label: modalNodeDef.label,
      parameters: modalParams,
      status: 'pending',
      results: null,
      onDelete: handleDeleteNode // Pass the delete handler here

    },
    // style: {
    //   background: '#0F172A',  // Very dark blue background
    //   color: '#94A3B8',      // Subtle gray for regular text
    //   border: '1px solid rgba(59, 130, 246, 0.2)', // Subtle blue border
    //   borderRadius: '8px',
    //   padding: '16px',
    //   boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    //   width: 200,
    //   '& .node-title': {  // Custom style for the title
    //     color: '#60A5FA',  // Bright blue for the title
    //     fontWeight: 500,
    //     fontSize: '14px',
    //     marginBottom: '8px'
    //   },
    //   '& .node-status': {  // Style for status text
    //     fontSize: '12px',
    //     color: '#64748B'
    //   },
    //   '& .node-icon': {  // Style for any icons
    //     color: '#60A5FA',
    //     marginRight: '8px'
    //   }
    // },
  
  };
    setNodes((prev) => [...prev, newNode]);
    
    handleCloseNodeConfigModal();
  };

  // Add this effect to track changes
useEffect(() => {
    
    
    if (nodes.length > 0 || edges.length > 0) {
      debouncedSave(nodes, edges);
    }
  }, [nodes, edges, debouncedSave]);

  // /////////////////////////////////////////////////////////////////////////////
  // // 2.5: RUN PIPELINE
  // /////////////////////////////////////////////////////////////////////////////
  const runPipeline = async () => {
    setLogs([]);

    // Pre-flight: if there is a generic trainModel node, ask AI what to replace it with
    const genericTrainNode = nodes.find(n => n.type === 'trainModel');
    if (genericTrainNode) {
      const uploadNode = nodes.find(n => n.type === 'uploadDataset');
      const configureNode = nodes.find(n => n.type === 'configureFeatures');

      const columns =
        uploadNode?.data?.results?.columns ||
        (configureNode?.data?.parameters?.feature_columns?.length > 0
          ? [
              ...(configureNode.data.parameters.feature_columns || []),
              configureNode.data.parameters.target_column,
            ].filter(Boolean)
          : null);

      setIsLogsOpen(true);
      setActiveTabOnOpen('logs');

      if (!columns || columns.length < 2) {
        setLogs(prev => [
          ...prev,
          '⚠️  The "Train Model" node is a placeholder and cannot be executed.',
          'To fix: delete it and add a specific model node (Classification, Regression, Clustering, etc.) from the panel.',
          'Or: configure Upload Dataset + Configure Features nodes and run again — AI will suggest the right model type.',
        ]);
        return;
      }

      setLogs(prev => [...prev, 'Detected generic Train Model node — consulting AI for the best model type...']);

      try {
        const res = await api.post('/automl/suggest', {
          datasetCharacteristics: {
            columnCount: columns.length,
            columns,
            rowCount: uploadNode?.data?.results?.rows || 0,
            featureColumns: columns.slice(0, -1),
            targetColumn: columns[columns.length - 1],
          },
          problemType: 'auto - infer from column names and dataset characteristics',
        });
        const rec = res.data?.suggestions?.primaryRecommendation;
        if (rec?.model_type) {
          setAiSuggestion({ ...rec, sourceNodeId: genericTrainNode.id });
          setLogs(prev => [
            ...prev,
            `AI suggests: "${rec.model_type.replace(/_/g, ' ')}" (${Math.round((rec.confidence || 0) * 100)}% confidence).`,
            'Apply the suggestion to replace the node, then run the pipeline again.',
          ]);
        } else {
          setLogs(prev => [
            ...prev,
            '⚠️  Could not get an AI suggestion. Delete the "Train Model" node and add a specific model type manually.',
          ]);
        }
      } catch (err) {
        setLogs(prev => [
          ...prev,
          '⚠️  AI suggestion failed. Delete the "Train Model" node and add a specific model type manually.',
        ]);
      }
      return;
    }

    setLogs((prev) => [...prev, 'Starting pipeline run...']);
    let currentDatasetId = datasetId;
    const operations = [];

    setIsLogsOpen(true);
    setActiveTabOnOpen('logs');

  
    // Build execution graph from edges
    const graph = {};
    const inDegree = {};
    nodes.forEach(node => {
      graph[node.id] = [];
      inDegree[node.id] = 0;
    });
  
    // Create adjacency list and calculate in-degrees
    edges.forEach(edge => {
      graph[edge.source].push(edge.target);
      inDegree[edge.target] = (inDegree[edge.target] || 0) + 1;
    });
  
    // Find start nodes (nodes with no incoming edges)
    const startNodes = Object.keys(inDegree).filter(id => inDegree[id] === 0);
    if (startNodes.length === 0) {
      setLogs((prev) => [...prev, 'Error: No start node found in pipeline']);
      return;
    }
  
    // Topological sort to get execution order
    const executionOrder = [];
    const queue = [...startNodes];
  
    while (queue.length > 0) {
      const currentId = queue.shift();
      executionOrder.push(currentId);
  
      for (const neighborId of graph[currentId]) {
        inDegree[neighborId]--;
        if (inDegree[neighborId] === 0) {
          queue.push(neighborId);
        }
      }
    }
  
    // Execute nodes in order
    for (const nodeId of executionOrder) {
      const node = nodes.find(n => n.id === nodeId);
      if (!node) continue;
      const startTime = new Date();
      const operation = {
        type: node.type,
        timestamp: startTime,
        parameters: node.data.parameters,
        status: 'pending',
        logs: []
      };
  
      try {

        setLogs((prev) => [...prev, `__STEP__:${node.data.label}`]);
        const initialLogsLength = logs.length;

        // console.log('Executing node:', node);

        switch (node.type) {
          case 'uploadDataset': {
  const { dataset } = node.data.parameters;
  if (!dataset) {
    throw new Error('No dataset selected.');
  }
  
  try {
    console.log("dataset._id", dataset._id);
    console.log("Dataset type:", dataset.metadata?.type);
    console.log("Attempting to sync dataset:", dataset._id);

    // Check if it's an image dataset or CSV dataset
    const isImageDataset = dataset.metadata?.type === 'image_classification';
    
    let response;
    if (isImageDataset) {
      console.log("Syncing image dataset to FastAPI...");
      response = await api.get(`/datasets/${dataset._id}/syncImageToFastAPI`);
    } else {
      console.log("Syncing CSV dataset to FastAPI...");
      response = await api.get(`/datasets/${dataset._id}/syncToFastAPI`);
    }

    console.log("FastAPI sync response:", response.data);

    let updatedNode;
    
    if (isImageDataset) {
      // For image datasets
      const { dataset_id, classes, total_images } = response.data;
      
      dataset.dataset_id = dataset_id;
      currentDatasetId = dataset_id;
      setDatasetId(currentDatasetId);
      
      updatedNode = {
        ...node,
        data: {
          ...node.data,
          status: 'completed',
          results: {
            dataset_id: dataset_id,
            classes: classes,
            totalImages: total_images,
            type: 'image_classification'
          }
        }
      };
      
      setLogs((prev) => [
        ...prev,
        `Using image dataset: ${dataset.name}`,
        `Classes: ${classes.length} classes (${classes.join(', ')})`,
        `Total images: ${total_images}`
      ]);
      
    } else {
      // For CSV datasets (existing logic)
      const { dataset_id, columns, shape, preview } = response.data;

      dataset.dataset_id = dataset_id;
      dataset.metadata = {
        rowCount: shape[0],
        columns,
      };
      currentDatasetId = dataset_id;
      setDatasetId(currentDatasetId);
      
      updatedNode = {
        ...node,
        data: {
          ...node.data,
          status: 'completed',
          results: {
            rows: dataset.metadata.rowCount,
            columns: dataset.metadata.columns
          }
        }
      };
      
      setLogs((prev) => [
        ...prev,
        `Using dataset: ${dataset.name}`,
        `Rows: ${dataset.metadata.rowCount}`,
        `Columns: ${dataset.metadata.columns.length}`
      ]);

    }

    setNodes((nds) =>
      nds.map((n) => (n.id === node.id ? updatedNode : n))
    );
    
  } catch (err) {
    console.error('Error syncing dataset to FastAPI:', err);
    throw new Error(`Failed to sync dataset: ${err.message}`);
  }
  break;
}
  
          
          case 'configureFeatures': {
            try {
              // Find the upload dataset node
              const uploadNode = nodes.find(n => n.type === 'uploadDataset');
              if (!uploadNode || !uploadNode.data.parameters.dataset) {
                throw new Error('Please add and configure an Upload Dataset node first');
              }
          
              const datasetId = uploadNode.data.parameters.dataset._id;
            
              
              // Get dataset content
              const response = await api.get(`/datasets/${datasetId}`);
              const csvLines = response.data.split('\n');
              const availableColumns = csvLines[0].split(',').map(col => col.trim());
          
              const { feature_columns, target_column } = node.data.parameters;
              
              // Validate columns exist in dataset
              const invalidColumns = [
                ...feature_columns,
                target_column
              ].filter(col => !availableColumns.includes(col));
          
              if (invalidColumns.length > 0) {
                throw new Error(`Invalid columns: ${invalidColumns.join(', ')}`);
              }
              
              
              // Configure features
              await mlApi.post(`/features/configure?dataset_id=${currentDatasetId}`, {
                feature_columns,
                target_column,
              });
              
              // Update node status
              const updatedNode = {
                ...node,
                data: {
                  ...node.data,
                  status: 'completed',
                  results: {
                    features: feature_columns,
                    target: target_column,
                    availableColumns
                  }
                }
              };
              
              setNodes((nds) => 
                nds.map((n) => (n.id === node.id ? updatedNode : n))
              );
              
              setLogs((prev) => [
                ...prev,
                'Features configured:',
                `• Available columns: ${availableColumns.join(', ')}`,
                `• Selected features: ${feature_columns.join(', ')}`,
                `• Target: ${target_column}`
              ]);
          
            } catch (error) {
              console.error('Error configuring features:', error);
              setLogs((prev) => [...prev, `Error configuring features: ${error.message}`]);
              throw error;
            }
            break;
          }
  
          case 'impute': {
            if (!currentDatasetId) {
              throw new Error('No dataset ID available');
            }
            
            const { numeric_impute_strategy } = node.data.parameters;
            await mlApi.post(`/impute?dataset_id=${currentDatasetId}`, {
              numeric_impute_strategy,
            });

  const updatedNode = {
    ...node,
    data: {
      ...node.data,
      status: 'completed',
      results: {
        strategy: numeric_impute_strategy
      }
    }
  };
  
  setNodes((nds) => 
    nds.map((n) => (n.id === node.id ? updatedNode : n))
  );
            
            setLogs((prev) => [...prev, 'Data Imputed']);
            break;
          }
  
          case 'scale': {
            if (!currentDatasetId) {
              throw new Error('No dataset ID available');
            }
            
            await mlApi.post(`/preprocess/scale?dataset_id=${currentDatasetId}`, 
              node.data.parameters
            );

            // Update node status and results
  const updatedNode = {
    ...node,
    data: {
      ...node.data,
      status: 'completed',
      results: {
        ...node.data.parameters
      }
    }
  };
  
  setNodes((nds) => 
    nds.map((n) => (n.id === node.id ? updatedNode : n))
  );
            
            setLogs((prev) => [...prev, 'Data scaled']);
            break;
          }
  
          case 'encode': {
            if (!currentDatasetId) {
              throw new Error('No dataset ID available');
            }
            
            await mlApi.post(`/preprocess/encode?dataset_id=${currentDatasetId}`, 
              node.data.parameters
            );

            // Update node status and results
  const updatedNode = {
    ...node,
    data: {
      ...node.data,
      status: 'completed',
      results: {
        ...node.data.parameters
      }
    }
  };
  
  setNodes((nds) => 
    nds.map((n) => (n.id === node.id ? updatedNode : n))
  );
            
            setLogs((prev) => [...prev, 'Categories encoded']);
            break;
          }
  
          case 'selectFeatures': {
            if (!currentDatasetId) {
              throw new Error('No dataset ID available');
            }
            
            await mlApi.post(`/preprocess/select_features?dataset_id=${currentDatasetId}`, 
              node.data.parameters
            );

            // Update node status and results
  const updatedNode = {
    ...node,
    data: {
      ...node.data,
      status: 'completed',
      results: {
        ...node.data.parameters
      }
    }
  };
  
  setNodes((nds) => 
    nds.map((n) => (n.id === node.id ? updatedNode : n))
  );
            
            setLogs((prev) => [...prev, 'Features selected']);
            break;
          }
  
          case 'reduceDimensions': {
            if (!currentDatasetId) {
              throw new Error('No dataset ID available');
            }
            
            await mlApi.post(`/preprocess/reduce_dimensions?dataset_id=${currentDatasetId}`, 
              node.data.parameters
            );

            // Update node status and results
  const updatedNode = {
    ...node,
    data: {
      ...node.data,
      status: 'completed',
      results: {
        ...node.data.parameters
      }
    }
  };
  
  setNodes((nds) => 
    nds.map((n) => (n.id === node.id ? updatedNode : n))
  );
            
            setLogs((prev) => [...prev, 'Dimensions reduced']);
            break;
          }
  
          case 'trainClassification':{
            if (!currentDatasetId) {
              throw new Error('No dataset ID available');
            }

            setLogs((prev) => [...prev, `Starting ${node.data.label} training...`]);
            
            // Get all parameters from node data
            const trainingParams = {
              // Common parameters
              model_type: node.data.parameters.model_type,
              test_size: node.data.parameters.test_size || 0.2,
              random_state: node.data.parameters.random_state || 42,
              
              // Include all other parameters as they are
              ...node.data.parameters,
              
              // Optimization and explainability
              enable_optimization: node.data.parameters.enable_optimization || false,
              enable_explanations: node.data.parameters.enable_explanations || false
            };

            // Add optimization config if enabled
            if (trainingParams.enable_optimization && node.data.parameters.optimization) {
              trainingParams.optimization = node.data.parameters.optimization;
            }

            let response;
            let endpoint = '/train';

            // Choose endpoint based on optimization
            if (trainingParams.enable_optimization) {
              endpoint = '/train/optimize';
              setLogs((prev) => [...prev, `Using hyperparameter optimization (${trainingParams.optimization?.method || 'random'})...`]);
            }

            // Route to appropriate endpoint based on model category and type
            if (node.type === 'trainDeepLearning') {
              if (['mlp'].includes(trainingParams.model_type)) {
                const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/neural_network' : '/train/neural_network';
                response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
              } else if (['lstm'].includes(trainingParams.model_type)) {
                const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/lstm' : '/train/lstm';
                response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
              }
            } else if (node.type === 'trainTimeSeries') {
              const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/time_series' : '/train/time_series';
              response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            } else if (node.type === 'trainClustering') {
              const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/clustering' : '/train/clustering';
              response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            } else {
              // Traditional ML models (classification/regression)
              response = await mlApi.post(`${endpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            }

            const { model_type, metrics, model, model_id } = response.data;

            // Handle optimization results (same as before)
            if (response.data.optimization_results) {
              const opt = response.data.optimization_results;
              setLogs((prev) => [
                ...prev,
                `🚀 Hyperparameter optimization complete!`,
                `• Method: ${opt.method}`,
                `• Best parameters: ${JSON.stringify(opt.best_params)}`,
                `• Improvement: ${opt.improvement.percentage > 0 ? '+' : ''}${opt.improvement.percentage.toFixed(2)}%`,
                `• Best CV score: ${opt.best_score.toFixed(4)}`
              ]);
            }

            // Handle explainability (same as before)
            if (trainingParams.enable_explanations) {
              try {
                setLogs((prev) => [...prev, `Generating model explanations...`]);
                
                const explanationResponse = await mlApi.post(`/explain/model?dataset_id=${currentDatasetId}`, {
                  model: model,
                  model_type: model_type,
                  type: 'both',
                  sample_size: 100
                });
                
                const explanations = explanationResponse.data.explanations;
                setModelExplanations(explanations);
                setCurrentExplanations(explanations);
                setCurrentModelType(model_type);
                
                setLogs((prev) => [...prev, `✨ Model explanations generated successfully`]);
                response.data.explanations = explanations;
                
              } catch (explError) {
                setLogs((prev) => [...prev, `Warning: Could not generate explanations: ${explError.message}`]);
              }
            }
            // Handle different types of results (your existing logic)
  if (model_type && model_type.includes('clustering')) {
    // Clustering results
    setLogs((prev) => [
      ...prev,
      `Clustering complete:`,
      `• Algorithm: ${trainingParams.model_type}`,
      `• Number of clusters: ${metrics.n_clusters || 'N/A'}`,
      `• Silhouette score: ${metrics.silhouette_score ? metrics.silhouette_score.toFixed(4) : 'N/A'}`,
      ...(metrics.n_noise_points !== undefined ? [`• Noise points: ${metrics.n_noise_points}`] : [])
    ]);
  } else if (model_type && model_type.includes('time_series')) {
    // Time series results
    if (metrics.aic && metrics.bic) {
      setLogs((prev) => [
        ...prev,
        `Time series training complete:`,
        `• Model: ${trainingParams.model_type.toUpperCase()}`,
        `• AIC: ${metrics.aic.toFixed(4)}`,
        `• BIC: ${metrics.bic.toFixed(4)}`,
        `• Forecast generated for ${trainingParams.forecast_periods} periods`
      ]);
    } else {
      setLogs((prev) => [
        ...prev,
        `Time series training complete:`,
        `• Model: ${trainingParams.model_type.toUpperCase()}`,
        `• Forecast generated for ${trainingParams.forecast_periods} periods`
      ]);
    }
  } else if (model_type && model_type === 'lstm') {
    // LSTM results - handle both classification and regression
    const logMessages = [`LSTM training complete:`];
    
    if (metrics.accuracy !== undefined) {
      // Classification results
      logMessages.push(`• Accuracy: ${(metrics.accuracy * 100).toFixed(2)}%`);
      
      if (metrics.classification_report && metrics.classification_report['weighted avg']) {
        const weightedAvg = metrics.classification_report['weighted avg'];
        logMessages.push(`• Precision: ${(weightedAvg.precision * 100).toFixed(2)}%`);
        logMessages.push(`• Recall: ${(weightedAvg.recall * 100).toFixed(2)}%`);
        logMessages.push(`• F1-Score: ${(weightedAvg['f1-score'] * 100).toFixed(2)}%`);
      }
    } else {
      // Regression results
      if (metrics.mse !== undefined) {
        logMessages.push(`• MSE: ${metrics.mse.toFixed(4)}`);
      }
      if (metrics.mae !== undefined) {
        logMessages.push(`• MAE: ${metrics.mae.toFixed(4)}`);
      }
      if (metrics.r2 !== undefined) {
        logMessages.push(`• R² Score: ${metrics.r2.toFixed(4)}`);
      }
    }
    
    if (metrics.final_loss !== undefined) {
      logMessages.push(`• Final Training Loss: ${metrics.final_loss.toFixed(4)}`);
    }
    if (metrics.final_val_loss !== undefined) {
      logMessages.push(`• Final Validation Loss: ${metrics.final_val_loss.toFixed(4)}`);
    }
    
    setLogs((prev) => [...prev, ...logMessages]);
  } else if (model_type && model_type.includes('neural_network')) {
    // Neural Network results
    if (metrics.accuracy) {
      setLogs((prev) => [
        ...prev,
        `Neural Network training complete:`,
        `• Architecture: ${trainingParams.hidden_layers.join(' → ')} neurons`,
        `• Accuracy: ${(metrics.accuracy * 100).toFixed(2)}%`,
        `• Activation: ${trainingParams.activation}`,
        `• Learning Rate: ${trainingParams.learning_rate}`
      ]);
    } else {
      setLogs((prev) => [
        ...prev,
        `Neural Network training complete:`,
        `• Architecture: ${trainingParams.hidden_layers.join(' → ')} neurons`,
        `• MSE: ${metrics.mse.toFixed(4)}`,
        `• R² Score: ${metrics.r2.toFixed(4)}`,
        `• MAE: ${metrics.mae.toFixed(4)}`
      ]);
    }
  } else {
    // Traditional ML results
    const isRegression = ['linear_regression', 'ridge', 'lasso', 'svr', 'elastic_net'].includes(trainingParams.model_type);
    
    if (isRegression) {
      const { mse, r2, mae } = metrics;
      setLogs((prev) => [
        ...prev,
        `Training complete:`,
        `• Mean Squared Error: ${mse.toFixed(4)}`,
        `• R² Score: ${r2.toFixed(4)}`,
        `• Mean Absolute Error: ${mae.toFixed(4)}`
      ]);
    } else {
      // Classification results
      const { accuracy, classification_report } = metrics;
      const weightedAvg = classification_report?.['weighted avg'] || {};
      
      const logMessages = [`Training complete:`];
      if (response.data.optimization_results) {
        logMessages.push(`• Optimized ${model_type.replace('_optimized', '')} model`);
      }
      logMessages.push(`• Accuracy: ${(accuracy * 100).toFixed(2)}%`);
      if (weightedAvg.precision) {
        logMessages.push(
          `• Detailed Metrics:`,
          `  - Precision: ${(weightedAvg.precision * 100).toFixed(2)}%`,
          `  - Recall: ${(weightedAvg.recall * 100).toFixed(2)}%`,
          `  - F1-Score: ${(weightedAvg['f1-score'] * 100).toFixed(2)}%`
        );
      }

      setLogs((prev) => [...prev, ...logMessages]);
    }
  }
            // Handle different types of results (reuse existing logic)
            // ... [keep all the existing result handling logic from the original trainModel case]

            // Update node status and results
            const updatedNode = {
              ...node,
              data: {
                ...node.data,
                status: 'completed',
                results: {
                  ...metrics,
                  model_id: model_id,
                  model: model,
                  model_type: model_type,
                  optimization_results: response.data.optimization_results || null,
                  explanations: response.data.explanations || null,
                  is_optimized: response.data.is_optimized || false
                }
              }
            };
            
            setNodes((nds) => 
              nds.map((n) => (n.id === node.id ? updatedNode : n))
            );

            if (model_id) {
              setTrainedModelId(model_id);
            }

            break;
          }
          case 'trainRegression':{
            if (!currentDatasetId) {
              throw new Error('No dataset ID available');
            }

            setLogs((prev) => [...prev, `Starting ${node.data.label} training...`]);
            
            // Get all parameters from node data
            const trainingParams = {
              // Common parameters
              model_type: node.data.parameters.model_type,
              test_size: node.data.parameters.test_size || 0.2,
              random_state: node.data.parameters.random_state || 42,
              
              // Include all other parameters as they are
              ...node.data.parameters,
              
              // Optimization and explainability
              enable_optimization: node.data.parameters.enable_optimization || false,
              enable_explanations: node.data.parameters.enable_explanations || false
            };

            // Add optimization config if enabled
            if (trainingParams.enable_optimization && node.data.parameters.optimization) {
              trainingParams.optimization = node.data.parameters.optimization;
            }

            let response;
            let endpoint = '/train';

            // Choose endpoint based on optimization
            if (trainingParams.enable_optimization) {
              endpoint = '/train/optimize';
              setLogs((prev) => [...prev, `Using hyperparameter optimization (${trainingParams.optimization?.method || 'random'})...`]);
            }

            // Route to appropriate endpoint based on model category and type
            if (node.type === 'trainDeepLearning') {
              if (['mlp'].includes(trainingParams.model_type)) {
                const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/neural_network' : '/train/neural_network';
                response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
              } else if (['lstm'].includes(trainingParams.model_type)) {
                const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/lstm' : '/train/lstm';
                response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
              }
            } else if (node.type === 'trainTimeSeries') {
              const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/time_series' : '/train/time_series';
              response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            } else if (node.type === 'trainClustering') {
              const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/clustering' : '/train/clustering';
              response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            } else {
              // Traditional ML models (classification/regression)
              response = await mlApi.post(`${endpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            }

            const { model_type, metrics, model, model_id } = response.data;

            // Handle optimization results (same as before)
            if (response.data.optimization_results) {
              const opt = response.data.optimization_results;
              setLogs((prev) => [
                ...prev,
                `🚀 Hyperparameter optimization complete!`,
                `• Method: ${opt.method}`,
                `• Best parameters: ${JSON.stringify(opt.best_params)}`,
                `• Improvement: ${opt.improvement.percentage > 0 ? '+' : ''}${opt.improvement.percentage.toFixed(2)}%`,
                `• Best CV score: ${opt.best_score.toFixed(4)}`
              ]);
            }

            // Handle explainability (same as before)
            if (trainingParams.enable_explanations) {
              try {
                setLogs((prev) => [...prev, `Generating model explanations...`]);
                
                const explanationResponse = await mlApi.post(`/explain/model?dataset_id=${currentDatasetId}`, {
                  model: model,
                  model_type: model_type,
                  type: 'both',
                  sample_size: 100
                });
                
                const explanations = explanationResponse.data.explanations;
                setModelExplanations(explanations);
                setCurrentExplanations(explanations);
                setCurrentModelType(model_type);
                
                setLogs((prev) => [...prev, `✨ Model explanations generated successfully`]);
                response.data.explanations = explanations;
                
              } catch (explError) {
                setLogs((prev) => [...prev, `Warning: Could not generate explanations: ${explError.message}`]);
              }
            }
            // Handle different types of results (your existing logic)
  if (model_type && model_type.includes('clustering')) {
    // Clustering results
    setLogs((prev) => [
      ...prev,
      `Clustering complete:`,
      `• Algorithm: ${trainingParams.model_type}`,
      `• Number of clusters: ${metrics.n_clusters || 'N/A'}`,
      `• Silhouette score: ${metrics.silhouette_score ? metrics.silhouette_score.toFixed(4) : 'N/A'}`,
      ...(metrics.n_noise_points !== undefined ? [`• Noise points: ${metrics.n_noise_points}`] : [])
    ]);
  } else if (model_type && model_type.includes('time_series')) {
    // Time series results
    if (metrics.aic && metrics.bic) {
      setLogs((prev) => [
        ...prev,
        `Time series training complete:`,
        `• Model: ${trainingParams.model_type.toUpperCase()}`,
        `• AIC: ${metrics.aic.toFixed(4)}`,
        `• BIC: ${metrics.bic.toFixed(4)}`,
        `• Forecast generated for ${trainingParams.forecast_periods} periods`
      ]);
    } else {
      setLogs((prev) => [
        ...prev,
        `Time series training complete:`,
        `• Model: ${trainingParams.model_type.toUpperCase()}`,
        `• Forecast generated for ${trainingParams.forecast_periods} periods`
      ]);
    }
  } else if (model_type && model_type === 'lstm') {
    // LSTM results - handle both classification and regression
    const logMessages = [`LSTM training complete:`];
    
    if (metrics.accuracy !== undefined) {
      // Classification results
      logMessages.push(`• Accuracy: ${(metrics.accuracy * 100).toFixed(2)}%`);
      
      if (metrics.classification_report && metrics.classification_report['weighted avg']) {
        const weightedAvg = metrics.classification_report['weighted avg'];
        logMessages.push(`• Precision: ${(weightedAvg.precision * 100).toFixed(2)}%`);
        logMessages.push(`• Recall: ${(weightedAvg.recall * 100).toFixed(2)}%`);
        logMessages.push(`• F1-Score: ${(weightedAvg['f1-score'] * 100).toFixed(2)}%`);
      }
    } else {
      // Regression results
      if (metrics.mse !== undefined) {
        logMessages.push(`• MSE: ${metrics.mse.toFixed(4)}`);
      }
      if (metrics.mae !== undefined) {
        logMessages.push(`• MAE: ${metrics.mae.toFixed(4)}`);
      }
      if (metrics.r2 !== undefined) {
        logMessages.push(`• R² Score: ${metrics.r2.toFixed(4)}`);
      }
    }
    
    if (metrics.final_loss !== undefined) {
      logMessages.push(`• Final Training Loss: ${metrics.final_loss.toFixed(4)}`);
    }
    if (metrics.final_val_loss !== undefined) {
      logMessages.push(`• Final Validation Loss: ${metrics.final_val_loss.toFixed(4)}`);
    }
    
    setLogs((prev) => [...prev, ...logMessages]);
  } else if (model_type && model_type.includes('neural_network')) {
    // Neural Network results
    if (metrics.accuracy) {
      setLogs((prev) => [
        ...prev,
        `Neural Network training complete:`,
        `• Architecture: ${trainingParams.hidden_layers.join(' → ')} neurons`,
        `• Accuracy: ${(metrics.accuracy * 100).toFixed(2)}%`,
        `• Activation: ${trainingParams.activation}`,
        `• Learning Rate: ${trainingParams.learning_rate}`
      ]);
    } else {
      setLogs((prev) => [
        ...prev,
        `Neural Network training complete:`,
        `• Architecture: ${trainingParams.hidden_layers.join(' → ')} neurons`,
        `• MSE: ${metrics.mse.toFixed(4)}`,
        `• R² Score: ${metrics.r2.toFixed(4)}`,
        `• MAE: ${metrics.mae.toFixed(4)}`
      ]);
    }
  } else {
    // Traditional ML results
    const isRegression = ['linear_regression', 'ridge', 'lasso', 'svr', 'elastic_net'].includes(trainingParams.model_type);
    
    if (isRegression) {
      const { mse, r2, mae } = metrics;
      setLogs((prev) => [
        ...prev,
        `Training complete:`,
        `• Mean Squared Error: ${mse.toFixed(4)}`,
        `• R² Score: ${r2.toFixed(4)}`,
        `• Mean Absolute Error: ${mae.toFixed(4)}`
      ]);
    } else {
      // Classification results
      const { accuracy, classification_report } = metrics;
      const weightedAvg = classification_report?.['weighted avg'] || {};
      
      const logMessages = [`Training complete:`];
      if (response.data.optimization_results) {
        logMessages.push(`• Optimized ${model_type.replace('_optimized', '')} model`);
      }
      logMessages.push(`• Accuracy: ${(accuracy * 100).toFixed(2)}%`);
      if (weightedAvg.precision) {
        logMessages.push(
          `• Detailed Metrics:`,
          `  - Precision: ${(weightedAvg.precision * 100).toFixed(2)}%`,
          `  - Recall: ${(weightedAvg.recall * 100).toFixed(2)}%`,
          `  - F1-Score: ${(weightedAvg['f1-score'] * 100).toFixed(2)}%`
        );
      }

      setLogs((prev) => [...prev, ...logMessages]);
    }
  }
            // Handle different types of results (reuse existing logic)
            // ... [keep all the existing result handling logic from the original trainModel case]

            // Update node status and results
            const updatedNode = {
              ...node,
              data: {
                ...node.data,
                status: 'completed',
                results: {
                  ...metrics,
                  model_id: model_id,
                  model: model,
                  model_type: model_type,
                  optimization_results: response.data.optimization_results || null,
                  explanations: response.data.explanations || null,
                  is_optimized: response.data.is_optimized || false
                }
              }
            };
            
            setNodes((nds) => 
              nds.map((n) => (n.id === node.id ? updatedNode : n))
            );

            if (model_id) {
              setTrainedModelId(model_id);
            }

            break;
          }
          case 'trainTimeSeries':{
            if (!currentDatasetId) {
              throw new Error('No dataset ID available');
            }

            setLogs((prev) => [...prev, `Starting ${node.data.label} training...`]);
            
            // Get all parameters from node data
            const trainingParams = {
              // Common parameters
              model_type: node.data.parameters.model_type,
              test_size: node.data.parameters.test_size || 0.2,
              random_state: node.data.parameters.random_state || 42,
              
              // Include all other parameters as they are
              ...node.data.parameters,
              
              // Optimization and explainability
              enable_optimization: node.data.parameters.enable_optimization || false,
              enable_explanations: node.data.parameters.enable_explanations || false
            };

            // Add optimization config if enabled
            if (trainingParams.enable_optimization && node.data.parameters.optimization) {
              trainingParams.optimization = node.data.parameters.optimization;
            }

            let response;
            let endpoint = '/train';

            // Choose endpoint based on optimization
            if (trainingParams.enable_optimization) {
              endpoint = '/train/optimize';
              setLogs((prev) => [...prev, `Using hyperparameter optimization (${trainingParams.optimization?.method || 'random'})...`]);
            }

            // Route to appropriate endpoint based on model category and type
            if (node.type === 'trainDeepLearning') {
              if (['mlp'].includes(trainingParams.model_type)) {
                const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/neural_network' : '/train/neural_network';
                response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
              } else if (['lstm'].includes(trainingParams.model_type)) {
                const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/lstm' : '/train/lstm';
                response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
              }
            } else if (node.type === 'trainTimeSeries') {
              const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/time_series' : '/train/time_series';
              response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            } else if (node.type === 'trainClustering') {
              const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/clustering' : '/train/clustering';
              response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            } else {
              // Traditional ML models (classification/regression)
              response = await mlApi.post(`${endpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            }

            const { model_type, metrics, model, model_id } = response.data;

            // Handle optimization results (same as before)
            if (response.data.optimization_results) {
              const opt = response.data.optimization_results;
              setLogs((prev) => [
                ...prev,
                `🚀 Hyperparameter optimization complete!`,
                `• Method: ${opt.method}`,
                `• Best parameters: ${JSON.stringify(opt.best_params)}`,
                `• Improvement: ${opt.improvement.percentage > 0 ? '+' : ''}${opt.improvement.percentage.toFixed(2)}%`,
                `• Best CV score: ${opt.best_score.toFixed(4)}`
              ]);
            }

            // Handle explainability (same as before)
            if (trainingParams.enable_explanations) {
              try {
                setLogs((prev) => [...prev, `Generating model explanations...`]);
                
                const explanationResponse = await mlApi.post(`/explain/model?dataset_id=${currentDatasetId}`, {
                  model: model,
                  model_type: model_type,
                  type: 'both',
                  sample_size: 100
                });
                
                const explanations = explanationResponse.data.explanations;
                setModelExplanations(explanations);
                setCurrentExplanations(explanations);
                setCurrentModelType(model_type);
                
                setLogs((prev) => [...prev, `✨ Model explanations generated successfully`]);
                response.data.explanations = explanations;
                
              } catch (explError) {
                setLogs((prev) => [...prev, `Warning: Could not generate explanations: ${explError.message}`]);
              }
            }
            // Handle different types of results (your existing logic)
  if (model_type && model_type.includes('clustering')) {
    // Clustering results
    setLogs((prev) => [
      ...prev,
      `Clustering complete:`,
      `• Algorithm: ${trainingParams.model_type}`,
      `• Number of clusters: ${metrics.n_clusters || 'N/A'}`,
      `• Silhouette score: ${metrics.silhouette_score ? metrics.silhouette_score.toFixed(4) : 'N/A'}`,
      ...(metrics.n_noise_points !== undefined ? [`• Noise points: ${metrics.n_noise_points}`] : [])
    ]);
  } else if (model_type && model_type.includes('time_series')) {
    // Time series results
    if (metrics.aic && metrics.bic) {
      setLogs((prev) => [
        ...prev,
        `Time series training complete:`,
        `• Model: ${trainingParams.model_type.toUpperCase()}`,
        `• AIC: ${metrics.aic.toFixed(4)}`,
        `• BIC: ${metrics.bic.toFixed(4)}`,
        `• Forecast generated for ${trainingParams.forecast_periods} periods`
      ]);
    } else {
      setLogs((prev) => [
        ...prev,
        `Time series training complete:`,
        `• Model: ${trainingParams.model_type.toUpperCase()}`,
        `• Forecast generated for ${trainingParams.forecast_periods} periods`
      ]);
    }
  } else if (model_type && model_type === 'lstm') {
    // LSTM results - handle both classification and regression
    const logMessages = [`LSTM training complete:`];
    
    if (metrics.accuracy !== undefined) {
      // Classification results
      logMessages.push(`• Accuracy: ${(metrics.accuracy * 100).toFixed(2)}%`);
      
      if (metrics.classification_report && metrics.classification_report['weighted avg']) {
        const weightedAvg = metrics.classification_report['weighted avg'];
        logMessages.push(`• Precision: ${(weightedAvg.precision * 100).toFixed(2)}%`);
        logMessages.push(`• Recall: ${(weightedAvg.recall * 100).toFixed(2)}%`);
        logMessages.push(`• F1-Score: ${(weightedAvg['f1-score'] * 100).toFixed(2)}%`);
      }
    } else {
      // Regression results
      if (metrics.mse !== undefined) {
        logMessages.push(`• MSE: ${metrics.mse.toFixed(4)}`);
      }
      if (metrics.mae !== undefined) {
        logMessages.push(`• MAE: ${metrics.mae.toFixed(4)}`);
      }
      if (metrics.r2 !== undefined) {
        logMessages.push(`• R² Score: ${metrics.r2.toFixed(4)}`);
      }
    }
    
    if (metrics.final_loss !== undefined) {
      logMessages.push(`• Final Training Loss: ${metrics.final_loss.toFixed(4)}`);
    }
    if (metrics.final_val_loss !== undefined) {
      logMessages.push(`• Final Validation Loss: ${metrics.final_val_loss.toFixed(4)}`);
    }
    
    setLogs((prev) => [...prev, ...logMessages]);
  } else if (model_type && model_type.includes('neural_network')) {
    // Neural Network results
    if (metrics.accuracy) {
      setLogs((prev) => [
        ...prev,
        `Neural Network training complete:`,
        `• Architecture: ${trainingParams.hidden_layers.join(' → ')} neurons`,
        `• Accuracy: ${(metrics.accuracy * 100).toFixed(2)}%`,
        `• Activation: ${trainingParams.activation}`,
        `• Learning Rate: ${trainingParams.learning_rate}`
      ]);
    } else {
      setLogs((prev) => [
        ...prev,
        `Neural Network training complete:`,
        `• Architecture: ${trainingParams.hidden_layers.join(' → ')} neurons`,
        `• MSE: ${metrics.mse.toFixed(4)}`,
        `• R² Score: ${metrics.r2.toFixed(4)}`,
        `• MAE: ${metrics.mae.toFixed(4)}`
      ]);
    }
  } else {
    // Traditional ML results
    const isRegression = ['linear_regression', 'ridge', 'lasso', 'svr', 'elastic_net'].includes(trainingParams.model_type);
    
    if (isRegression) {
      const { mse, r2, mae } = metrics;
      setLogs((prev) => [
        ...prev,
        `Training complete:`,
        `• Mean Squared Error: ${mse.toFixed(4)}`,
        `• R² Score: ${r2.toFixed(4)}`,
        `• Mean Absolute Error: ${mae.toFixed(4)}`
      ]);
    } else {
      // Classification results
      const { accuracy, classification_report } = metrics;
      const weightedAvg = classification_report?.['weighted avg'] || {};
      
      const logMessages = [`Training complete:`];
      if (response.data.optimization_results) {
        logMessages.push(`• Optimized ${model_type.replace('_optimized', '')} model`);
      }
      logMessages.push(`• Accuracy: ${(accuracy * 100).toFixed(2)}%`);
      if (weightedAvg.precision) {
        logMessages.push(
          `• Detailed Metrics:`,
          `  - Precision: ${(weightedAvg.precision * 100).toFixed(2)}%`,
          `  - Recall: ${(weightedAvg.recall * 100).toFixed(2)}%`,
          `  - F1-Score: ${(weightedAvg['f1-score'] * 100).toFixed(2)}%`
        );
      }

      setLogs((prev) => [...prev, ...logMessages]);
    }
  }
            // Handle different types of results (reuse existing logic)
            // ... [keep all the existing result handling logic from the original trainModel case]

            // Update node status and results
            const updatedNode = {
              ...node,
              data: {
                ...node.data,
                status: 'completed',
                results: {
                  ...metrics,
                  model_id: model_id,
                  model: model,
                  model_type: model_type,
                  optimization_results: response.data.optimization_results || null,
                  explanations: response.data.explanations || null,
                  is_optimized: response.data.is_optimized || false
                }
              }
            };
            
            setNodes((nds) => 
              nds.map((n) => (n.id === node.id ? updatedNode : n))
            );

            if (model_id) {
              setTrainedModelId(model_id);
            }

            break;
          }
          case 'trainDeepLearning':{
            if (!currentDatasetId) {
              throw new Error('No dataset ID available');
            }

            setLogs((prev) => [...prev, `Starting ${node.data.label} training...`]);
            
            // Get all parameters from node data
            const trainingParams = {
              // Common parameters
              model_type: node.data.parameters.model_type,
              test_size: node.data.parameters.test_size || 0.2,
              random_state: node.data.parameters.random_state || 42,
              
              // Include all other parameters as they are
              ...node.data.parameters,
              
              // Optimization and explainability
              enable_optimization: node.data.parameters.enable_optimization || false,
              enable_explanations: node.data.parameters.enable_explanations || false
            };

            // Add optimization config if enabled
            if (trainingParams.enable_optimization && node.data.parameters.optimization) {
              trainingParams.optimization = node.data.parameters.optimization;
            }

            let response;
            let endpoint = '/train';

            // Choose endpoint based on optimization
            if (trainingParams.enable_optimization) {
              endpoint = '/train/optimize';
              setLogs((prev) => [...prev, `Using hyperparameter optimization (${trainingParams.optimization?.method || 'random'})...`]);
            }

            // Route to appropriate endpoint based on model category and type
            if (node.type === 'trainDeepLearning') {
              if (['mlp'].includes(trainingParams.model_type)) {
                const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/neural_network' : '/train/neural_network';
                response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
              } else if (['lstm'].includes(trainingParams.model_type)) {
                const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/lstm' : '/train/lstm';
                response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
              }
            } else if (node.type === 'trainTimeSeries') {
              const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/time_series' : '/train/time_series';
              response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            } else if (node.type === 'trainClustering') {
              const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/clustering' : '/train/clustering';
              response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            } else {
              // Traditional ML models (classification/regression)
              response = await mlApi.post(`${endpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            }

            const { model_type, metrics, model, model_id } = response.data;

            // Handle optimization results (same as before)
            if (response.data.optimization_results) {
              const opt = response.data.optimization_results;
              setLogs((prev) => [
                ...prev,
                `🚀 Hyperparameter optimization complete!`,
                `• Method: ${opt.method}`,
                `• Best parameters: ${JSON.stringify(opt.best_params)}`,
                `• Improvement: ${opt.improvement.percentage > 0 ? '+' : ''}${opt.improvement.percentage.toFixed(2)}%`,
                `• Best CV score: ${opt.best_score.toFixed(4)}`
              ]);
            }

            // Handle explainability (same as before)
            if (trainingParams.enable_explanations) {
              try {
                setLogs((prev) => [...prev, `Generating model explanations...`]);
                
                const explanationResponse = await mlApi.post(`/explain/model?dataset_id=${currentDatasetId}`, {
                  model: model,
                  model_type: model_type,
                  type: 'both',
                  sample_size: 100
                });
                
                const explanations = explanationResponse.data.explanations;
                setModelExplanations(explanations);
                setCurrentExplanations(explanations);
                setCurrentModelType(model_type);
                
                setLogs((prev) => [...prev, `✨ Model explanations generated successfully`]);
                response.data.explanations = explanations;
                
              } catch (explError) {
                setLogs((prev) => [...prev, `Warning: Could not generate explanations: ${explError.message}`]);
              }
            }
            // Handle different types of results (your existing logic)
  if (model_type && model_type.includes('clustering')) {
    // Clustering results
    setLogs((prev) => [
      ...prev,
      `Clustering complete:`,
      `• Algorithm: ${trainingParams.model_type}`,
      `• Number of clusters: ${metrics.n_clusters || 'N/A'}`,
      `• Silhouette score: ${metrics.silhouette_score ? metrics.silhouette_score.toFixed(4) : 'N/A'}`,
      ...(metrics.n_noise_points !== undefined ? [`• Noise points: ${metrics.n_noise_points}`] : [])
    ]);
  } else if (model_type && model_type.includes('time_series')) {
    // Time series results
    if (metrics.aic && metrics.bic) {
      setLogs((prev) => [
        ...prev,
        `Time series training complete:`,
        `• Model: ${trainingParams.model_type.toUpperCase()}`,
        `• AIC: ${metrics.aic.toFixed(4)}`,
        `• BIC: ${metrics.bic.toFixed(4)}`,
        `• Forecast generated for ${trainingParams.forecast_periods} periods`
      ]);
    } else {
      setLogs((prev) => [
        ...prev,
        `Time series training complete:`,
        `• Model: ${trainingParams.model_type.toUpperCase()}`,
        `• Forecast generated for ${trainingParams.forecast_periods} periods`
      ]);
    }
  } else if (model_type && model_type === 'lstm') {
    // LSTM results - handle both classification and regression
    const logMessages = [`LSTM training complete:`];
    
    if (metrics.accuracy !== undefined) {
      // Classification results
      logMessages.push(`• Accuracy: ${(metrics.accuracy * 100).toFixed(2)}%`);
      
      if (metrics.classification_report && metrics.classification_report['weighted avg']) {
        const weightedAvg = metrics.classification_report['weighted avg'];
        logMessages.push(`• Precision: ${(weightedAvg.precision * 100).toFixed(2)}%`);
        logMessages.push(`• Recall: ${(weightedAvg.recall * 100).toFixed(2)}%`);
        logMessages.push(`• F1-Score: ${(weightedAvg['f1-score'] * 100).toFixed(2)}%`);
      }
    } else {
      // Regression results
      if (metrics.mse !== undefined) {
        logMessages.push(`• MSE: ${metrics.mse.toFixed(4)}`);
      }
      if (metrics.mae !== undefined) {
        logMessages.push(`• MAE: ${metrics.mae.toFixed(4)}`);
      }
      if (metrics.r2 !== undefined) {
        logMessages.push(`• R² Score: ${metrics.r2.toFixed(4)}`);
      }
    }
    
    if (metrics.final_loss !== undefined) {
      logMessages.push(`• Final Training Loss: ${metrics.final_loss.toFixed(4)}`);
    }
    if (metrics.final_val_loss !== undefined) {
      logMessages.push(`• Final Validation Loss: ${metrics.final_val_loss.toFixed(4)}`);
    }
    
    setLogs((prev) => [...prev, ...logMessages]);
  } else if (model_type && model_type.includes('neural_network')) {
    // Neural Network results
    if (metrics.accuracy) {
      setLogs((prev) => [
        ...prev,
        `Neural Network training complete:`,
        `• Architecture: ${trainingParams.hidden_layers.join(' → ')} neurons`,
        `• Accuracy: ${(metrics.accuracy * 100).toFixed(2)}%`,
        `• Activation: ${trainingParams.activation}`,
        `• Learning Rate: ${trainingParams.learning_rate}`
      ]);
    } else {
      setLogs((prev) => [
        ...prev,
        `Neural Network training complete:`,
        `• Architecture: ${trainingParams.hidden_layers.join(' → ')} neurons`,
        `• MSE: ${metrics.mse.toFixed(4)}`,
        `• R² Score: ${metrics.r2.toFixed(4)}`,
        `• MAE: ${metrics.mae.toFixed(4)}`
      ]);
    }
  } else {
    // Traditional ML results
    const isRegression = ['linear_regression', 'ridge', 'lasso', 'svr', 'elastic_net'].includes(trainingParams.model_type);
    
    if (isRegression) {
      const { mse, r2, mae } = metrics;
      setLogs((prev) => [
        ...prev,
        `Training complete:`,
        `• Mean Squared Error: ${mse.toFixed(4)}`,
        `• R² Score: ${r2.toFixed(4)}`,
        `• Mean Absolute Error: ${mae.toFixed(4)}`
      ]);
    } else {
      // Classification results
      const { accuracy, classification_report } = metrics;
      const weightedAvg = classification_report?.['weighted avg'] || {};
      
      const logMessages = [`Training complete:`];
      if (response.data.optimization_results) {
        logMessages.push(`• Optimized ${model_type.replace('_optimized', '')} model`);
      }
      logMessages.push(`• Accuracy: ${(accuracy * 100).toFixed(2)}%`);
      if (weightedAvg.precision) {
        logMessages.push(
          `• Detailed Metrics:`,
          `  - Precision: ${(weightedAvg.precision * 100).toFixed(2)}%`,
          `  - Recall: ${(weightedAvg.recall * 100).toFixed(2)}%`,
          `  - F1-Score: ${(weightedAvg['f1-score'] * 100).toFixed(2)}%`
        );
      }

      setLogs((prev) => [...prev, ...logMessages]);
    }
  }
            // Handle different types of results (reuse existing logic)
            // ... [keep all the existing result handling logic from the original trainModel case]

            // Update node status and results
            const updatedNode = {
              ...node,
              data: {
                ...node.data,
                status: 'completed',
                results: {
                  ...metrics,
                  model_id: model_id,
                  model: model,
                  model_type: model_type,
                  optimization_results: response.data.optimization_results || null,
                  explanations: response.data.explanations || null,
                  is_optimized: response.data.is_optimized || false
                }
              }
            };
            
            setNodes((nds) => 
              nds.map((n) => (n.id === node.id ? updatedNode : n))
            );

            if (model_id) {
              setTrainedModelId(model_id);
            }

            break;
          }
          case 'trainClustering': {
            if (!currentDatasetId) {
              throw new Error('No dataset ID available');
            }

            setLogs((prev) => [...prev, `Starting ${node.data.label} training...`]);
            
            // Get all parameters from node data
            const trainingParams = {
              // Common parameters
              model_type: node.data.parameters.model_type,
              test_size: node.data.parameters.test_size || 0.2,
              random_state: node.data.parameters.random_state || 42,
              
              // Include all other parameters as they are
              ...node.data.parameters,
              
              // Optimization and explainability
              enable_optimization: node.data.parameters.enable_optimization || false,
              enable_explanations: node.data.parameters.enable_explanations || false
            };

            // Add optimization config if enabled
            if (trainingParams.enable_optimization && node.data.parameters.optimization) {
              trainingParams.optimization = node.data.parameters.optimization;
            }

            let response;
            let endpoint = '/train';

            // Choose endpoint based on optimization
            if (trainingParams.enable_optimization) {
              endpoint = '/train/optimize';
              setLogs((prev) => [...prev, `Using hyperparameter optimization (${trainingParams.optimization?.method || 'random'})...`]);
            }

            // Route to appropriate endpoint based on model category and type
            if (node.type === 'trainDeepLearning') {
              if (['mlp'].includes(trainingParams.model_type)) {
                const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/neural_network' : '/train/neural_network';
                response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
              } else if (['lstm'].includes(trainingParams.model_type)) {
                const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/lstm' : '/train/lstm';
                response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
              }
            } else if (node.type === 'trainTimeSeries') {
              const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/time_series' : '/train/time_series';
              response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            } else if (node.type === 'trainClustering') {
              const baseEndpoint = trainingParams.enable_optimization ? '/train/optimize/clustering' : '/train/clustering';
              response = await mlApi.post(`${baseEndpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            } else {
              // Traditional ML models (classification/regression)
              response = await mlApi.post(`${endpoint}?dataset_id=${currentDatasetId}`, trainingParams);
            }

            const { model_type, metrics, model, model_id } = response.data;

            // Handle optimization results (same as before)
            if (response.data.optimization_results) {
              const opt = response.data.optimization_results;
              setLogs((prev) => [
                ...prev,
                `🚀 Hyperparameter optimization complete!`,
                `• Method: ${opt.method}`,
                `• Best parameters: ${JSON.stringify(opt.best_params)}`,
                `• Improvement: ${opt.improvement.percentage > 0 ? '+' : ''}${opt.improvement.percentage.toFixed(2)}%`,
                `• Best CV score: ${opt.best_score.toFixed(4)}`
              ]);
            }

            // Handle explainability (same as before)
            if (trainingParams.enable_explanations) {
              try {
                setLogs((prev) => [...prev, `Generating model explanations...`]);
                
                const explanationResponse = await mlApi.post(`/explain/model?dataset_id=${currentDatasetId}`, {
                  model: model,
                  model_type: model_type,
                  type: 'both',
                  sample_size: 100
                });
                
                const explanations = explanationResponse.data.explanations;
                setModelExplanations(explanations);
                setCurrentExplanations(explanations);
                setCurrentModelType(model_type);
                
                setLogs((prev) => [...prev, `✨ Model explanations generated successfully`]);
                response.data.explanations = explanations;
                
              } catch (explError) {
                setLogs((prev) => [...prev, `Warning: Could not generate explanations: ${explError.message}`]);
              }
            }
            // Handle different types of results (your existing logic)
  if (model_type && model_type.includes('clustering')) {
    // Clustering results
    setLogs((prev) => [
      ...prev,
      `Clustering complete:`,
      `• Algorithm: ${trainingParams.model_type}`,
      `• Number of clusters: ${metrics.n_clusters || 'N/A'}`,
      `• Silhouette score: ${metrics.silhouette_score ? metrics.silhouette_score.toFixed(4) : 'N/A'}`,
      ...(metrics.n_noise_points !== undefined ? [`• Noise points: ${metrics.n_noise_points}`] : [])
    ]);
  } else if (model_type && model_type.includes('time_series')) {
    // Time series results
    if (metrics.aic && metrics.bic) {
      setLogs((prev) => [
        ...prev,
        `Time series training complete:`,
        `• Model: ${trainingParams.model_type.toUpperCase()}`,
        `• AIC: ${metrics.aic.toFixed(4)}`,
        `• BIC: ${metrics.bic.toFixed(4)}`,
        `• Forecast generated for ${trainingParams.forecast_periods} periods`
      ]);
    } else {
      setLogs((prev) => [
        ...prev,
        `Time series training complete:`,
        `• Model: ${trainingParams.model_type.toUpperCase()}`,
        `• Forecast generated for ${trainingParams.forecast_periods} periods`
      ]);
    }
  } else if (model_type && model_type === 'lstm') {
    // LSTM results - handle both classification and regression
    const logMessages = [`LSTM training complete:`];
    
    if (metrics.accuracy !== undefined) {
      // Classification results
      logMessages.push(`• Accuracy: ${(metrics.accuracy * 100).toFixed(2)}%`);
      
      if (metrics.classification_report && metrics.classification_report['weighted avg']) {
        const weightedAvg = metrics.classification_report['weighted avg'];
        logMessages.push(`• Precision: ${(weightedAvg.precision * 100).toFixed(2)}%`);
        logMessages.push(`• Recall: ${(weightedAvg.recall * 100).toFixed(2)}%`);
        logMessages.push(`• F1-Score: ${(weightedAvg['f1-score'] * 100).toFixed(2)}%`);
      }
    } else {
      // Regression results
      if (metrics.mse !== undefined) {
        logMessages.push(`• MSE: ${metrics.mse.toFixed(4)}`);
      }
      if (metrics.mae !== undefined) {
        logMessages.push(`• MAE: ${metrics.mae.toFixed(4)}`);
      }
      if (metrics.r2 !== undefined) {
        logMessages.push(`• R² Score: ${metrics.r2.toFixed(4)}`);
      }
    }
    
    if (metrics.final_loss !== undefined) {
      logMessages.push(`• Final Training Loss: ${metrics.final_loss.toFixed(4)}`);
    }
    if (metrics.final_val_loss !== undefined) {
      logMessages.push(`• Final Validation Loss: ${metrics.final_val_loss.toFixed(4)}`);
    }
    
    setLogs((prev) => [...prev, ...logMessages]);
  } else if (model_type && model_type.includes('neural_network')) {
    // Neural Network results
    if (metrics.accuracy) {
      setLogs((prev) => [
        ...prev,
        `Neural Network training complete:`,
        `• Architecture: ${trainingParams.hidden_layers.join(' → ')} neurons`,
        `• Accuracy: ${(metrics.accuracy * 100).toFixed(2)}%`,
        `• Activation: ${trainingParams.activation}`,
        `• Learning Rate: ${trainingParams.learning_rate}`
      ]);
    } else {
      setLogs((prev) => [
        ...prev,
        `Neural Network training complete:`,
        `• Architecture: ${trainingParams.hidden_layers.join(' → ')} neurons`,
        `• MSE: ${metrics.mse.toFixed(4)}`,
        `• R² Score: ${metrics.r2.toFixed(4)}`,
        `• MAE: ${metrics.mae.toFixed(4)}`
      ]);
    }
  } else {
    // Traditional ML results
    const isRegression = ['linear_regression', 'ridge', 'lasso', 'svr', 'elastic_net'].includes(trainingParams.model_type);
    
    if (isRegression) {
      const { mse, r2, mae } = metrics;
      setLogs((prev) => [
        ...prev,
        `Training complete:`,
        `• Mean Squared Error: ${mse.toFixed(4)}`,
        `• R² Score: ${r2.toFixed(4)}`,
        `• Mean Absolute Error: ${mae.toFixed(4)}`
      ]);
    } else {
      // Classification results
      const { accuracy, classification_report } = metrics;
      const weightedAvg = classification_report?.['weighted avg'] || {};
      
      const logMessages = [`Training complete:`];
      if (response.data.optimization_results) {
        logMessages.push(`• Optimized ${model_type.replace('_optimized', '')} model`);
      }
      logMessages.push(`• Accuracy: ${(accuracy * 100).toFixed(2)}%`);
      if (weightedAvg.precision) {
        logMessages.push(
          `• Detailed Metrics:`,
          `  - Precision: ${(weightedAvg.precision * 100).toFixed(2)}%`,
          `  - Recall: ${(weightedAvg.recall * 100).toFixed(2)}%`,
          `  - F1-Score: ${(weightedAvg['f1-score'] * 100).toFixed(2)}%`
        );
      }

      setLogs((prev) => [...prev, ...logMessages]);
    }
  }
            // Handle different types of results (reuse existing logic)
            // ... [keep all the existing result handling logic from the original trainModel case]

            // Update node status and results
            const updatedNode = {
              ...node,
              data: {
                ...node.data,
                status: 'completed',
                results: {
                  ...metrics,
                  model_id: model_id,
                  model: model,
                  model_type: model_type,
                  optimization_results: response.data.optimization_results || null,
                  explanations: response.data.explanations || null,
                  is_optimized: response.data.is_optimized || false
                }
              }
            };
            
            setNodes((nds) => 
              nds.map((n) => (n.id === node.id ? updatedNode : n))
            );

            if (model_id) {
              setTrainedModelId(model_id);
            }

            break;
          }

          case 'trainCNN': {
  if (!currentDatasetId) {
    throw new Error('No dataset ID available');
  }


  setLogs((prev) => [...prev, `Starting CNN training...`]);
  
  const cnnParams = {
    architecture: node.data.parameters.architecture || 'resnet50',
    img_size: node.data.parameters.img_size || 224,
    batch_size: node.data.parameters.batch_size || 32,
    epochs: node.data.parameters.epochs || 10,
    learning_rate: node.data.parameters.learning_rate || 0.001,
    fine_tune: node.data.parameters.fine_tune ?? true,
    augmentation: node.data.parameters.augmentation ?? true,
    test_size: node.data.parameters.test_size || 0.2,
  };

  try {
    // Check if we have an image dataset
    console.log('Starting CNN training case...');
  console.log('Current dataset ID:', currentDatasetId);

    const uploadNode = nodes.find(n => n.type === 'uploadDataset');
    console.log('Upload node found:', !!uploadNode);
  console.log('Dataset from upload node:', uploadNode?.data?.parameters?.dataset);
    if (!uploadNode || !uploadNode.data.parameters.dataset) {
      throw new Error('Please add and configure an image dataset first');
    }

    const dataset = uploadNode.data.parameters.dataset;
    
    if (dataset.metadata?.type !== 'image_classification') {
      throw new Error('CNN training requires an image dataset. Please upload an image dataset organized in folders by class.');
    }

    setLogs((prev) => [
      ...prev, 
      `Using image dataset: ${dataset.name}`,
      `Classes detected: ${dataset.metadata.classes.join(', ')}`,
      `Total images: ${dataset.metadata.totalImages}`,
      `Architecture: ${cnnParams.architecture}`
    ]);

    // Sync image dataset to FastAPI
    const syncResponse = await api.get(`/datasets/${dataset._id}/syncImageToFastAPI`);

    const imageDatasetId = syncResponse.data.dataset_id;
    
    setLogs((prev) => [...prev, `Image dataset synced to ML service`]);
    
    // Train CNN
    const response = await mlApi.post(`/train/cnn?dataset_id=${imageDatasetId}`, cnnParams);
    const { model_type, metrics, model, model_id } = response.data;

    // Log detailed results
    setLogs((prev) => [
      ...prev,
      `CNN training complete!`,
      `• Architecture: ${cnnParams.architecture}`,
      `• Validation Accuracy: ${(metrics.val_accuracy * 100).toFixed(2)}%`,
      `• Training Accuracy: ${(metrics.train_accuracy * 100).toFixed(2)}%`,
      `• Number of Classes: ${metrics.num_classes}`,
      `• Image Size: ${cnnParams.img_size}x${cnnParams.img_size}`,
      `• Epochs: ${cnnParams.epochs}`,
      ...(cnnParams.fine_tune ? [`• Fine-tuning enabled`] : []),
      ...(cnnParams.augmentation ? [`• Data augmentation applied`] : [])
    ]);

    // Update node with results
    const updatedNode = {
      ...node,
      data: {
        ...node.data,
        status: 'completed',
        results: {
          ...metrics,
          model_id: model_id,
          model: model,
          model_type: model_type
        }
      }
    };
    
    setNodes((nds) => 
      nds.map((n) => (n.id === node.id ? updatedNode : n))
    );

    if (model_id) {
      setTrainedModelId(model_id);
    }

  } catch (error) {
    console.error('CNN training error:', error);
    
    let errorMessage = 'CNN training failed';
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setLogs((prev) => [...prev, `Error: ${errorMessage}`]);
    
    const errorNode = {
      ...node,
      data: {
        ...node.data,
        status: 'error',
        results: { error: errorMessage }
      }
    };
    
    setNodes((nds) => 
      nds.map((n) => (n.id === node.id ? errorNode : n))
    );
    
    throw new Error(errorMessage);
  }

  break;
}
  
          default:
            setLogs((prev) => [...prev, `Unknown node type: ${node.type}`]);
        }
        setLogs((prev) => [...prev, '__DONE__']);
        operation.status = 'completed';
         // Get logs that were added during this operation
         operation.logs = logs.slice(initialLogsLength);
         operation.results = node.data.results;
      } catch (error) {
        setLogs((prev) => [...prev, `__ERROR__:${error.message}`]);
        return;
      }
      operations.push(operation);
    }
    const pipelineName = pipeline.name || 'Untitled Pipeline';

    // After successful pipeline execution, save logs
    const logData = {
      pipelineId,
      operations,
      userId: user._id,
      datasetId: currentDatasetId,
      modelId: trainedModelId,
      metrics: nodes.find(n => (n.type === 'trainCNN') || (n.type === 'trainDeepLearning') || (n.type === 'trainClustering') || (n.type === 'trainTimeSeries') || (n.type === 'trainClassification') || (n.type === 'trainRegression'))?.data?.results,
    };
    // If we have visualizations and a configured features node
    const configFeatures = nodes.find(n => n.type === 'configureFeatures');
    if (currentDatasetId && configFeatures?.data?.parameters) {
      try {
        const params = new URLSearchParams();
        configFeatures.data.parameters.feature_columns.forEach(column => {
          params.append('feature_columns', column);
        });
        params.append('target_column', configFeatures.data.parameters.target_column);
        
        const vizResponse = await mlApi.get(
          `/analytics/plots/${currentDatasetId}?${params.toString()}`
        );
        logData.visualizations = { plots: vizResponse.data };
      } catch (error) {
        console.error('Error fetching visualizations:', error);
      }
    }

    logData.pipelineName = pipelineName;

    // Save the log data
    try {
      // console.log('Saving logs:', logData);
      await api.post('/logs', logData);
    } catch (error) {
      console.error('Error saving logs:', error);
    }


  
    setLogs((prev) => [...prev, 'Pipeline run complete.']);
  };
  
  // Add this function to analyze the dataset when opening trainModel config
// Add this enhanced dataset analysis function to your PipelineEditor.js

const analyzeDatasetForAutoML = async () => {
  try {
    const uploadNode = nodes.find(n => n.type === 'uploadDataset');
    const configureNode = nodes.find(n => n.type === 'configureFeatures');
    
    console.log('Upload node:', uploadNode);
    console.log('Configure node:', configureNode);
    
    if (!uploadNode?.data?.parameters?.dataset || !configureNode?.data?.parameters) {
      console.log('Missing required nodes for analysis');
      return null;
    }

    const dataset = uploadNode.data.parameters.dataset;
    const featureConfig = configureNode.data.parameters;
    
    if (!featureConfig.target_column) {
      console.log('No target column configured');
      return null;
    }

    // Get dataset preview data to analyze target column
    let targetColumnData = null;
    let problemType = 'classification'; // default
    let targetType = 'categorical';
    
    try {
      // Fetch dataset content to analyze target column
      const datasetResponse = await api.get(`/datasets/${dataset._id}`);
      const csvLines = datasetResponse.data.split('\n');
      
      if (csvLines.length > 1) {
        const headers = csvLines[0].split(',').map(col => col.trim());
        const targetIndex = headers.indexOf(featureConfig.target_column);
        
        if (targetIndex !== -1) {
          // Extract target column values (sample from first 100 rows)
          const targetValues = [];
          const maxSampleSize = Math.min(100, csvLines.length - 1);
          
          for (let i = 1; i <= maxSampleSize; i++) {
            if (csvLines[i]) {
              const row = csvLines[i].split(',');
              if (row[targetIndex] && row[targetIndex].trim()) {
                targetValues.push(row[targetIndex].trim());
              }
            }
          }
          
          if (targetValues.length > 0) {
            const analysisResult = detectProblemType(targetValues, featureConfig.target_column);
            problemType = analysisResult.problemType;
            targetType = analysisResult.targetType;
            targetColumnData = analysisResult;
            
            console.log('Problem type detection result:', analysisResult);
          }
        }
      }
    } catch (error) {
      console.error('Error analyzing target column:', error);
      // Fall back to basic heuristics
      problemType = 'classification';
    }

    // Calculate column type distribution
    const columnTypes = analyzeColumnTypes(dataset.metadata?.columns || []);
    
    const characteristics = {
      rowCount: dataset.metadata?.rowCount || 0,
      columnCount: dataset.metadata?.columns?.length || 0,
      columnTypes: columnTypes,
      featureColumns: featureConfig.feature_columns || [],
      targetColumn: featureConfig.target_column || '',
      targetType: targetType,
      targetAnalysis: targetColumnData,
      missingValues: estimateMissingValues(dataset),
      datasetName: dataset.name,
      additionalInfo: `Dataset: ${dataset.name}, Detected problem: ${problemType}, Target: ${featureConfig.target_column}`
    };

    console.log('Generated characteristics:', characteristics);
    return { characteristics, problemType };
    
  } catch (error) {
    console.error('Error analyzing dataset:', error);
    return null;
  }
};

// Function to detect problem type based on target column values
const detectProblemType = (targetValues, columnName) => {
  console.log(`Analyzing target column "${columnName}" with ${targetValues.length} sample values`);
  
  // Remove empty values and convert to lowercase for analysis
  const cleanValues = targetValues
    .filter(val => val && val.toLowerCase() !== 'null' && val.toLowerCase() !== 'na')
    .map(val => val.toString().trim());
  
  if (cleanValues.length === 0) {
    return {
      problemType: 'classification',
      targetType: 'categorical',
      confidence: 0.5,
      reasoning: 'No valid target values found, defaulting to classification'
    };
  }

  // Count unique values
  const uniqueValues = [...new Set(cleanValues)];
  const uniqueCount = uniqueValues.length;
  const totalCount = cleanValues.length;
  const uniqueRatio = uniqueCount / totalCount;
  
  console.log(`Unique values: ${uniqueCount}, Total: ${totalCount}, Ratio: ${uniqueRatio.toFixed(3)}`);
  console.log('Sample unique values:', uniqueValues.slice(0, 10));

  // Analyze if values are numeric
  const numericValues = cleanValues.filter(val => !isNaN(parseFloat(val)) && isFinite(parseFloat(val)));
  const numericRatio = numericValues.length / cleanValues.length;
  
  console.log(`Numeric values: ${numericValues.length}/${cleanValues.length} (${(numericRatio * 100).toFixed(1)}%)`);

  // Decision logic for problem type detection
  let problemType = 'classification';
  let targetType = 'categorical';
  let confidence = 0.8;
  let reasoning = '';

  // Rule 1: If mostly non-numeric, it's classification
  if (numericRatio < 0.7) {
    problemType = 'classification';
    targetType = 'categorical';
    confidence = 0.9;
    reasoning = `Target contains mostly text/categorical values (${(numericRatio * 100).toFixed(1)}% numeric)`;
  }
  // Rule 2: If numeric but few unique values, likely classification
  else if (numericRatio >= 0.7 && uniqueCount <= 10) {
    problemType = 'classification';
    targetType = 'categorical';
    confidence = 0.85;
    reasoning = `Target has ${uniqueCount} unique numeric values, suggesting categorical labels`;
  }
  // Rule 3: If numeric and many unique values, likely regression
  else if (numericRatio >= 0.7 && uniqueRatio > 0.1) {
    problemType = 'regression';
    targetType = 'continuous';
    confidence = 0.9;
    reasoning = `Target has many unique numeric values (${uniqueCount} unique, ${(uniqueRatio * 100).toFixed(1)}% unique ratio)`;
  }
  // Rule 4: Binary classification detection
  else if (uniqueCount === 2) {
    problemType = 'classification';
    targetType = 'binary';
    confidence = 0.95;
    reasoning = `Target has exactly 2 unique values: [${uniqueValues.join(', ')}]`;
  }
  // Rule 5: Common classification patterns
  else {
    const classificationKeywords = ['yes', 'no', 'true', 'false', 'class', 'category', 'type', 'label'];
    const hasClassificationKeywords = uniqueValues.some(val => 
      classificationKeywords.some(keyword => 
        val.toLowerCase().includes(keyword)
      )
    );
    
    if (hasClassificationKeywords) {
      problemType = 'classification';
      targetType = 'categorical';
      confidence = 0.8;
      reasoning = 'Target contains classification-related keywords';
    } else {
      // Default based on numeric analysis
      problemType = uniqueCount > 20 ? 'regression' : 'classification';
      targetType = uniqueCount > 20 ? 'continuous' : 'categorical';
      confidence = 0.7;
      reasoning = `Based on ${uniqueCount} unique values, inferred as ${problemType}`;
    }
  }

  return {
    problemType,
    targetType,
    confidence,
    reasoning,
    uniqueCount,
    totalSamples: totalCount,
    numericRatio,
    uniqueRatio,
    sampleValues: uniqueValues.slice(0, 5), // First 5 unique values as examples
    statistics: numericRatio > 0.7 ? calculateNumericStats(numericValues.map(parseFloat)) : null
  };
};

// Helper function to calculate basic statistics for numeric targets
const calculateNumericStats = (numericValues) => {
  if (numericValues.length === 0) return null;
  
  const sorted = numericValues.sort((a, b) => a - b);
  const mean = numericValues.reduce((a, b) => a + b) / numericValues.length;
  const median = sorted[Math.floor(sorted.length / 2)];
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const range = max - min;
  
  return { mean, median, min, max, range };
};

// Helper function to analyze column types
const analyzeColumnTypes = (columns) => {
  if (!columns || !Array.isArray(columns)) {
    return { numerical: 0, categorical: 0, unknown: 0 };
  }
  
  // Simple heuristic based on column names
  let numerical = 0;
  let categorical = 0;
  let datetime = 0;
  
  columns.forEach(col => {
    const colLower = col.toLowerCase();
    
    // Numeric indicators
    if (colLower.includes('num') || colLower.includes('count') || 
        colLower.includes('age') || colLower.includes('price') || 
        colLower.includes('amount') || colLower.includes('score') ||
        colLower.includes('rate') || colLower.includes('percent')) {
      numerical++;
    }
    // Date indicators
    else if (colLower.includes('date') || colLower.includes('time') || 
             colLower.includes('year') || colLower.includes('month')) {
      datetime++;
    }
    // Categorical by default
    else {
      categorical++;
    }
  });
  
  return { numerical, categorical, datetime, total: columns.length };
};

// Helper function to estimate missing values
const estimateMissingValues = (dataset) => {
  // This is a placeholder - you could enhance this by actually analyzing the data
  if (dataset.metadata?.rowCount && dataset.metadata?.rowCount > 0) {
    // Assume some missing values based on dataset size
    if (dataset.metadata.rowCount < 100) return 'Low';
    if (dataset.metadata.rowCount < 10000) return 'Medium';
    return 'Low'; // Larger datasets usually have better data quality
  }
  return 'Unknown';
};
  
const handleShowExplanations = () => {
  console.log('👁️ Looking for model with explanations...');
  
  // Find the most recent completed trainModel node with explanations
  const trainNodes = nodes.filter(n => (n.type === 'trainCNN') || (n.type === 'trainDeepLearning') || (n.type === 'trainClustering') || (n.type === 'trainTimeSeries') || (n.type === 'trainClassification') || (n.type === 'trainRegression'));
  const completedWithExplanations = trainNodes.find(n => 
    n.data.status === 'completed' && n.data.results?.explanations
  );
  
  console.log('👁️ Found train nodes:', trainNodes.length);
  console.log('👁️ Node with explanations:', completedWithExplanations);
  
  if (completedWithExplanations) {
    console.log('👁️ Showing explanations for model:', completedWithExplanations.data.results.model_type);
    setCurrentExplanations(completedWithExplanations.data.results.explanations);
    setCurrentModelType(completedWithExplanations.data.results.model_type || 'Unknown');
    setShowExplanationsModal(true);
  } else {
    setNotification({ variant: 'info', title: 'No explanations available', message: 'Train a model with explanations enabled first.' });
  }
};

// 2. Function to check if any completed models have explanations
const hasModelExplanations = () => {
  return nodes.some(n => 
    ((n.type === 'trainCNN') || (n.type === 'trainDeepLearning') || (n.type === 'trainClustering') || (n.type === 'trainTimeSeries') || (n.type === 'trainClassification') || (n.type === 'trainRegression')) && 
    n.data.status === 'completed' && 
    n.data.results?.explanations
  );
};
  // Updated handleSaveModel function with size checking
const handleSaveModel = async () => {
  if (!trainedModelId) {
    return; 
  }
  
  try {
    // Find the train node (either CNN or traditional ML)
    const trainNode = nodes.find(n => 
      ((n.type === 'trainCNN') || (n.type === 'trainDeepLearning') || (n.type === 'trainClustering') || (n.type === 'trainTimeSeries') || (n.type === 'trainClassification') || (n.type === 'trainRegression'))
    );
    
    if (!trainNode || !trainNode.data.results) {
      setNotification({ variant: 'error', title: 'No model to save', message: 'No trained model found. Please run training first.' });
      return;
    }
    
    const results = trainNode.data.results;
    const isCNN = trainNode.type === 'trainCNN';
    
    // Validate model data exists and is a string
    if (!results.model || typeof results.model !== 'string') {
      console.error('Invalid model data:', typeof results.model);
      setNotification({ variant: 'error', title: 'Invalid model data', message: 'Model data is missing or corrupted. Please retrain the model.' });
      return;
    }
    
    // Check model size before sending
    const modelSizeBytes = results.model.length;
    const modelSizeMB = (modelSizeBytes * 0.75) / (1024 * 1024); // Base64 is ~33% larger than binary
    
    console.log(`Model size check: ${modelSizeMB.toFixed(2)} MB`);
    
    // Frontend size limit (should match backend)
    const MAX_SIZE_MB = 100;
    if (modelSizeMB > MAX_SIZE_MB) {
      setNotification({
        variant: 'error',
        title: `Model too large (${modelSizeMB.toFixed(2)} MB)`,
        message: `Maximum allowed size is ${MAX_SIZE_MB} MB.\n\nTry:\n• Reducing model complexity\n• Using fewer epochs\n• Smaller network architecture\n• Lower resolution images (for CNN)`,
      });
      return;
    }
    
    // Warn for large models
    if (modelSizeMB > 50) {
      const proceed = window.confirm(
        `This model is quite large (${modelSizeMB.toFixed(2)}MB).\n` +
        `Upload may take several minutes.\n\n` +
        `Do you want to continue?`
      );
      if (!proceed) return;
    }
    
    // Get dataset info for CNN models
    const uploadNode = nodes.find(n => n.type === 'uploadDataset');
    const dataset = uploadNode?.data?.parameters?.dataset;
    
    // Prepare save data based on model type
    let saveData = {
      name: pipeline?.name || 'Untitled Model',
      model_type: isCNN ? 'cnn' : results.model_type,
      metrics: results,
      model_id: results.model_id,
      model_b64: results.model,
      owner: user?._id
    };
    
    // Validate required data
    if (!saveData.model_b64) {
      setNotification({ variant: 'error', title: 'No model data', message: 'No model data available. Please train the model first.' });
      return;
    }

    if (!saveData.owner) {
      setNotification({ variant: 'error', title: 'Authentication error', message: 'User not authenticated. Please log in again.' });
      return;
    }

    // Add CNN-specific data
    if (isCNN) {
      if (!dataset || dataset.metadata?.type !== 'image_classification') {
        setNotification({ variant: 'error', title: 'Wrong dataset type', message: 'CNN model requires an image classification dataset.' });
        return;
      }
      
      saveData = {
        ...saveData,
        architecture: trainNode.data.parameters.architecture || 'resnet50',
        dataset_type: 'image_classification',
        classes: dataset.metadata.classes || [],
        training_params: {
          epochs: trainNode.data.parameters.epochs,
          batch_size: trainNode.data.parameters.batch_size,
          learning_rate: trainNode.data.parameters.learning_rate,
          img_size: trainNode.data.parameters.img_size,
          fine_tune: trainNode.data.parameters.fine_tune,
          augmentation: trainNode.data.parameters.augmentation,
          test_size: trainNode.data.parameters.test_size
        }
      };
      
      console.log('Saving CNN model:', {
        name: saveData.name,
        architecture: saveData.architecture,
        classes: saveData.classes.length + ' classes',
        dataset_type: saveData.dataset_type,
        size_mb: modelSizeMB.toFixed(2)
      });
    } else {
      console.log('Saving ML model:', {
        name: saveData.name,
        model_type: saveData.model_type,
        size_mb: modelSizeMB.toFixed(2)
      });
    }
    
    setIsModelSaving(true);
    setModelSaveSuccess(false);

    try {
      const response = await api.post('/models/save', saveData);

      if (response.data.success) {
        setModelSaveSuccess(true);
        setTimeout(() => setModelSaveSuccess(false), 2000);
        setNotification({
          variant: 'success',
          title: `${isCNN ? 'CNN' : 'ML'} model saved!`,
          message: `Size: ${response.data.file_size_mb} MB\nModel ID: ${response.data.model._id}`,
        });
      } else {
        setNotification({ variant: 'error', title: 'Save failed', message: response.data.error || 'Unknown error' });
      }
    } catch (apiError) {
      console.error('API Error:', apiError);
      let errorMessage = 'Failed to save model';
      if (apiError.response?.status === 413) {
        errorMessage = 'Model too large for server';
      } else if (apiError.response?.status === 400) {
        errorMessage = apiError.response.data.error || 'Invalid model data';
      } else if (apiError.response?.data?.error) {
        errorMessage = apiError.response.data.error;
      } else if (apiError.code === 'ECONNABORTED') {
        errorMessage = 'Upload timeout - model too large or slow connection';
      }
      setNotification({ variant: 'error', title: 'Save failed', message: errorMessage });
    } finally {
      setIsModelSaving(false);
    }

  } catch (error) {
    console.error('Unexpected error saving model:', error);
    setNotification({ variant: 'error', title: 'Unexpected error', message: error.message });
    setIsModelSaving(false);
  }
};


  /////////////////////////////////////////////////////////////////////////////
  // 3. RENDER
  /////////////////////////////////////////////////////////////////////////////
  return (
    <div className="h-full flex">
        
      {/* MIDDLE: React Flow Canvas */}
<div className="flex-1 relative bg-gray-900" ref={reactFlowWrapper}>
  <ReactFlow
    nodes={nodes}
    edges={edges}
    onNodesChange={onNodesChange}
    onEdgesChange={onEdgesChange}
    onConnect={onConnect}
    onInit={setReactFlowInstance}
    onDrop={onDrop}
    onDragOver={onDragOver}
    nodeTypes={nodeTypes}
    fitView
    onNodeClick={onNodeClick}
    className="transition-all duration-200"
   
  >
    <Controls className="bg-gray-800/50 backdrop-blur-md p-2 border border-gray-700/50 rounded-lg shadow-xl" />
    <MiniMap 
      className="bg-gray-800/50 backdrop-blur-md border border-gray-700/50 rounded-lg shadow-xl"
      nodeColor={(node) => {
        return node.data?.color || '#6366f1';
      }}
      maskColor="rgb(15, 23, 42, 0.5)"
    />
    <Background 
      variant="dots" 
      gap={12} 
      size={1} 
      color="#4b5563"
      className="bg-gray-900"
    />
  </ReactFlow>
</div>
      

      {/* RIGHT: Logs */}
      {/* <div className="w-64 border-l border-gray-200 p-4">
        <h3 className="text-lg font-semibold mb-2">Logs</h3>
        <div className="h-96 overflow-auto border p-2 bg-gray-50">
          {logs.map((log, idx) => (
            <div key={idx}>{log}</div>
          ))}
        </div>
      </div> */}
      <ExecutionLogs
        isOpen={isLogsOpen}
        onClose={() => setIsLogsOpen(false)}
        logs={logs}
        currentDatasetId={datasetId}
        nodes={nodes} 
        initialActiveTab={activeTabOnOpen} 
      />

      
      {/* MODAL: Add Node Panel */}
{showAddNodePanel && (
  <div
    className="absolute top-0 left-0 w-full h-full bg-gray-900/80 backdrop-blur-sm flex justify-center items-center p-4"
    onClick={handleCloseAddNodePanel}
  >
    <div
      className="bg-gray-800 w-3/4 h-3/4 rounded-xl border border-gray-700/50 shadow-2xl backdrop-blur-xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="p-6 border-b border-gray-700/50">
        <h2 className="text-2xl font-bold text-white">Add a Node</h2>
      </div>

      {/* Search Bar */}
      <div className="p-6 border-b border-gray-700/50">
        <div className="relative">
          <input
            type="text"
            placeholder="Search nodes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-900/50 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            {/* You can add a search icon here */}
          </div>
        </div>
      </div>

      {/* Node Categories */}
      <div className="p-6 overflow-auto max-h-[calc(100%-200px)]">
        {filteredCategories.map((cat) => {
          if (cat.nodes.length === 0) return null;
          return (
            <div key={cat.category} className="mb-8 last:mb-0">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                {cat.category}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {cat.nodes.map((nodeDef) => (
                  <button
                    key={nodeDef.type}
                    onClick={() => handleNodeDefSelect(nodeDef)}
                    className="group p-4 bg-gray-900/50 border border-gray-700/50 rounded-lg text-left hover:bg-gray-700/50 hover:border-cyan-500/50 transition-all duration-200"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-cyan-400 group-hover:text-cyan-300 transition-colors">
                        {nodeDef.icon}
                      </span>
                      <span className="text-gray-200 group-hover:text-white transition-colors">
                        {nodeDef.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
)}
      {/* Toolbar */}
      <Toolbar
  onAddNode={handleOpenAddNodePanel}
  onRun={runPipeline}
  trainedModelId={trainedModelId}
  onSaveModel={handleSaveModel}
  isModelSaving={isModelSaving}
  modelSaveSuccess={modelSaveSuccess}
  onOpenDataLogs={handleOpenDataLogs}
  onShowExplanations={handleShowExplanations}
  hasExplanations={hasModelExplanations()}
/>
      <button 
        onClick={() => setIsLogsOpen(true)}
        className="fixed bottom-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg"
      >
        View Logs
      </button>
      

      

      {/* MODAL: Node Config (creates or edits a node) */}
      {showNodeConfigModal && modalNodeDef && (
        <div
          className="absolute inset-0 bg-black bg-opacity-40 flex justify-center items-center"
          onClick={handleCloseNodeConfigModal}
        >
          <div
            className="bg-gray-900 w-65 text-white p-6 rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">Configure {modalNodeDef.label}</h2>

            {/* Render form fields based on type */}
            {modalNodeDef.type === 'uploadDataset' && (
  <div>
    <label className="block mb-2">Select or Upload Dataset:</label>
    <DatasetSelector
      onSelect={(dataset) => handleParamChange('dataset', dataset)}
    />
    {modalParams.dataset && (
      <p className="mt-1 text-sm text-gray-600">
        Selected dataset: {modalParams.dataset.name}
      </p>
    )}
  </div>
)}


{modalNodeDef.type === 'configureFeatures' && (
  <div>
    <div className="mb-4">
      {nodes.some(n => n.type === 'uploadDataset') ? (
        <AvailableColumns 
          datasetId={nodes.find(n => 
            n.type === 'uploadDataset' && 
            n.data?.parameters?.dataset?._id
          )?.data?.parameters?.dataset?._id}
          selectedFeatures={modalParams.feature_columns}
          targetColumn={modalParams.target_column}
          onFeatureSelect={(columns) => handleParamChange('feature_columns', columns)}
          onTargetSelect={(column) => handleParamChange('target_column', column)}
        />
      ) : (
        <div className="text-sm text-amber-600">
          Please add an Upload Dataset node first
        </div>
      )}
    </div>
  </div>
)}

            {modalNodeDef.type === 'impute' && (
              <div>
                <label className="block font-semibold mb-1">Numeric Impute Strategy</label>
                <select
                  className="border w-full text-black p-1"
                  value={modalParams.numeric_impute_strategy}
                  onChange={(e) =>
                    handleParamChange('numeric_impute_strategy', e.target.value)
                  }
                >
                  <option value="mean">mean</option>
                  <option value="median">median</option>
                  <option value="most_frequent">most_frequent</option>
                </select>
              </div>
            )}

      {modalNodeDef.type === 'scale' && (
        <div>
          <label className="block font-semibold mb-1">Scaling Method</label>
          <select
            className="border w-full text-black mb-2 p-1"
            value={modalParams.method}
            onChange={(e) => handleParamChange('method', e.target.value)}
          >
            <option value="standard">Standard Scaling</option>
            <option value="minmax">Min-Max Scaling</option>
            <option value="robust">Robust Scaling</option>
            <option value="normalizer">Normalizer</option>
          </select>

          {modalParams.method === 'minmax' && (
            <>
              <label className="block font-semibold mb-1">Feature Range</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.1"
                  className="border w-1/2 p-1"
                  placeholder="Min"
                  value={modalParams.feature_range_min}
                  onChange={(e) => handleParamChange('feature_range_min', parseFloat(e.target.value))}
                />
                <input
                  type="number"
                  step="0.1"
                  className="border w-1/2 p-1"
                  placeholder="Max"
                  value={modalParams.feature_range_max}
                  onChange={(e) => handleParamChange('feature_range_max', parseFloat(e.target.value))}
                />
              </div>
            </>
          )}
        </div>
              )}

        {modalNodeDef.type === 'encode' && (
          <div>
            <label className="block font-semibold mb-1">Encoding Method</label>
            <select
              className="border w-full text-black mb-2 p-1"
              value={modalParams.method}
              onChange={(e) => handleParamChange('method', e.target.value)}
            >
              <option value="onehot">One-Hot Encoding</option>
              <option value="label">Label Encoding</option>
              <option value="ordinal">Ordinal Encoding</option>
            </select>

            <label className="block font-semibold mb-1">Handle Unknown</label>
            <select
              className="border w-full text-black mb-2 p-1"
              value={modalParams.handle_unknown}
              onChange={(e) => handleParamChange('handle_unknown', e.target.value)}
            >
              <option value="error">Error</option>
              <option value="ignore">Ignore</option>
            </select>
          </div>
        )}

        {modalNodeDef.type === 'selectFeatures' && (
          <div>
            <label className="block font-semibold mb-1">Selection Method</label>
            <select
              className="border w-full text-black mb-2 p-1"
              value={modalParams.method}
              onChange={(e) => handleParamChange('method', e.target.value)}
            >
              <option value="variance">Variance Threshold</option>
              <option value="kbest">K Best</option>
              <option value="percentile">Percentile</option>
            </select>

            {modalParams.method === 'variance' && (
              <>
                <label className="block font-semibold mb-1">Variance Threshold</label>
                <input
                  type="number"
                  step="0.01"
                  className="border w-full text-black mb-2 p-1"
                  value={modalParams.threshold}
                  onChange={(e) => handleParamChange('threshold', parseFloat(e.target.value))}
                />
              </>
            )}

            {modalParams.method === 'kbest' && (
              <>
                <label className="block font-semibold mb-1">Number of Features (K)</label>
                <input
                  type="number"
                  className="border w-full text-black mb-2 p-1"
                  value={modalParams.k}
                  onChange={(e) => handleParamChange('k', parseInt(e.target.value))}
                />
              </>
            )}

            {modalParams.method === 'percentile' && (
              <>
                <label className="block font-semibold mb-1">Percentile</label>
                <input
                  type="number"
                  className="border w-full text-black mb-2 p-1"
                  min="1"
                  max="100"
                  value={modalParams.percentile}
                  onChange={(e) => handleParamChange('percentile', parseInt(e.target.value))}
                />
              </>
            )}
          </div>
        )}

        {modalNodeDef.type === 'reduceDimensions' && (
          <div>
            <label className="block font-semibold mb-1">Reduction Method</label>
            <select
              className="border w-full text-black mb-2 p-1"
              value={modalParams.method}
              onChange={(e) => handleParamChange('method', e.target.value)}
            >
              <option value="pca">PCA</option>
              <option value="tsne">t-SNE</option>
              <option value="umap">UMAP</option>
            </select>

            <label className="block font-semibold mb-1">Number of Components</label>
            <input
              type="number"
              className="border w-full text-black mb-2 p-1"
              min="2"
              value={modalParams.n_components}
              onChange={(e) => handleParamChange('n_components', parseInt(e.target.value))}
            />

            <label className="block font-semibold mb-1">Random State</label>
            <input
              type="number"
              className="border w-full text-black mb-2 p-1"
              value={modalParams.random_state}
              onChange={(e) => handleParamChange('random_state', parseInt(e.target.value))}
            />
          </div>
        )}
{modalNodeDef.type === 'trainClassification' && (
  <div className="space-y-6 max-w-[1000px] max-h-[500px] overflow-y-auto bg-gray-900 text-white p-6 rounded-lg">
    {/* Reuse most of the existing trainModel modal content */}
    {/* Dataset Analysis Status - same as before */}
    <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/50 rounded-lg p-4 backdrop-blur-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">📊</span>
          <span className="font-semibold text-gray-100 text-lg">Dataset Analysis</span>
        </div>
        <button
          onClick={async () => {
            const analysis = await analyzeDatasetForAutoML();
            setDatasetAnalysis(analysis);
            if (analysis?.problemType) {
              setDetectedProblemType(analysis.problemType);
              setManualProblemType(analysis.problemType);
            }
          }}
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg transition-all duration-200 font-medium shadow-lg"
        >
          {datasetAnalysis ? 'Re-analyze' : 'Analyze Dataset'}
        </button>
      </div>
      
      {!datasetAnalysis && (
        <div className="mt-3 text-sm text-gray-300">
          Click "Analyze Dataset" to detect problem type and get AI suggestions
        </div>
      )}
    </div>
    {/* Problem Type Detection - same as before */}
    {datasetAnalysis?.characteristics?.targetAnalysis && (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-xl bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            🎯 Problem Type Detection
          </h4>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowProblemTypeHelp(!showProblemTypeHelp)}
              className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
            >
              How does this work?
            </button>
            <button
              onClick={() => setOverrideProblemType(!overrideProblemType)}
              className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium ${
                overrideProblemType 
                  ? 'bg-orange-600/20 text-orange-400 hover:bg-orange-600/30 border border-orange-500/30' 
                  : 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-500/30'
              }`}
            >
              {overrideProblemType ? 'Use Auto-Detection' : 'Manual Override'}
            </button>
          </div>
        </div>

        {/* Help Text */}
        {showProblemTypeHelp && (
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 text-sm text-blue-200 backdrop-blur-sm">
            <strong className="text-blue-100">How Problem Type Detection Works:</strong>
            <ul className="mt-2 space-y-2 text-blue-300">
              <li>• <strong>Classification:</strong> For predicting categories (e.g., spam/not spam, product types)</li>
              <li>• <strong>Regression:</strong> For predicting continuous numbers (e.g., prices, temperatures)</li>
              <li>• The system analyzes your target column's unique values, data types, and patterns</li>
              <li>• High confidence (80%) means the detection is reliable</li>
            </ul>
          </div>
        )}

        <ProblemTypeVisualizer 
          targetAnalysis={datasetAnalysis.characteristics.targetAnalysis}
          targetColumn={datasetAnalysis.characteristics.targetColumn}
        />

        {/* Manual Override Section */}
        {overrideProblemType && (
          <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-orange-400 text-xl">⚠️</span>
              <span className="font-semibold text-orange-200 text-lg">Manual Problem Type Override</span>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Problem Type:
                </label>
                <select
                  value={manualProblemType}
                  onChange={(e) => setManualProblemType(e.target.value)}
                  className="w-full bg-gray-800 border border-orange-500/30 rounded-lg px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="classification">Classification</option>
                  <option value="regression">Regression</option>
                </select>
              </div>
              <div className="text-sm text-orange-300 bg-orange-900/20 p-3 rounded-lg border border-orange-500/20">
                <strong>Note:</strong> Manual override will change the available models and AI suggestions.
                Make sure this matches your actual prediction goal.
              </div>
            </div>
          </div>
        )}
      </div>
    )}
    {/* AutoML Section - same as before */}
    {datasetAnalysis && (
      <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/30 rounded-lg p-5 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">🤖</span>
            <span className="font-bold text-xl bg-gradient-to-r from-blue-300 to-indigo-300 bg-clip-text text-transparent">
              AI Model Recommendations
            </span>
            <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs rounded-full font-bold shadow-lg">
              SMART
            </span>
          </div>
          <button
            onClick={() => setShowAutoML(!showAutoML)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              showAutoML 
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 shadow-lg' 
                : 'bg-gray-800 text-blue-400 border border-blue-500/30 hover:bg-gray-700'
            }`}
          >
            {showAutoML ? 'Hide AI Suggestions' : 'Get AI Suggestions'}
          </button>
        </div>
        
        <p className="text-blue-300 text-sm mb-3">
          Based on your {overrideProblemType ? manualProblemType : datasetAnalysis.problemType} problem 
          with {datasetAnalysis.characteristics.rowCount} samples and {datasetAnalysis.characteristics.columnCount} features.
        </p>

        {showAutoML && (
          <AutoMLSuggestion
            datasetCharacteristics={datasetAnalysis.characteristics}
            currentProblemType={overrideProblemType ? manualProblemType : datasetAnalysis.problemType}
            onModelSelect={(modelType) => {
              handleParamChange('model_type', modelType);
            }}
            onParametersSelect={(parameters) => {
              Object.entries(parameters).forEach(([key, value]) => {
                handleParamChange(key, value);
              });
            }}
            visible={showAutoML}
          />
        )}
      </div>
    )}
    {/* Hyperparameter Optimization - same as before */}
    <div className="bg-gradient-to-r from-purple-900/40 to-violet-900/40 border border-purple-500/30 rounded-lg p-5 backdrop-blur-sm">
      <HyperparameterOptimizer
        modelType={modalParams.model_type}
        isEnabled={enableOptimization}
        onOptimizationToggle={(enabled) => {
          setEnableOptimization(enabled);
          handleParamChange('enable_optimization', enabled);
          if (enabled) {
            handleParamChange('optimization', optimizationConfig);
          } else {
            handleParamChange('optimization', null);
          }
        }}
        onConfigChange={(config) => {
          setOptimizationConfig(config);
          if (enableOptimization) {
            handleParamChange('optimization', config);
          }
        }}
        className="mb-0"
      />
    </div>
    {/* Model Explainability - same as before */}
 <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 border border-green-500/30 rounded-lg p-5 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">🔍</span>
          <span className="font-bold text-xl bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">
            Model Explainability
          </span>
          <span className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs rounded-full font-bold shadow-lg">
            INSIGHTS
          </span>
        </div>
        
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enableExplanations}
            onChange={(e) => {
              console.log('🔍 EXPLAINABILITY CHECKBOX CHANGED:', e.target.checked);
              console.log('🔍 Current modalParams before change:', modalParams);
              setEnableExplanations(e.target.checked);
              handleParamChange('enable_explanations', e.target.checked);
              console.log('🔍 EnableExplanations state set to:', e.target.checked);
            }}
            className="w-5 h-5 text-green-500 bg-gray-800 border-green-500/30 rounded focus:ring-green-500 focus:ring-2"
          />
          <span className="text-sm font-medium text-green-300">
            Generate Explanations
          </span>
        </label>
      </div>
      
      {/* DEBUG: Show current states - Styled for dark theme */}
      <div className="text-xs text-gray-500 mb-3 bg-gray-800/50 p-3 rounded-lg border border-gray-600/30">
        <strong className="text-gray-400">Debug Info:</strong><br/>
        <span className="text-gray-400">enableExplanations state:</span> <span className="text-green-400">{enableExplanations.toString()}</span><br/>
        <span className="text-gray-400">modalParams.enable_explanations:</span> <span className="text-green-400">{modalParams.enable_explanations?.toString() || 'undefined'}</span><br/>
        <span className="text-gray-400">modalParams keys:</span> <span className="text-blue-400">{Object.keys(modalParams).join(', ')}</span>
      </div>
      
      <div className="text-green-300 text-sm">
        <p className="mb-3">
          Understand your model's decisions with SHAP and LIME explanations:
        </p>
        <ul className="space-y-2 text-green-400">
          <li>• <strong>Feature Importance:</strong> Which features matter most</li>
          <li>• <strong>SHAP Values:</strong> How each feature impacts predictions</li>
          <li>• <strong>LIME Analysis:</strong> Local explanations for individual predictions</li>
        </ul>
        
        {enableExplanations && (
          <div className="mt-4 p-3 bg-green-900/30 rounded-lg border border-green-500/30">
            <div className="flex items-center space-x-2">
              <span className="text-green-400 text-lg">✅</span>
              <span className="text-sm text-green-200">
                <strong>Enabled:</strong> Explanations will be generated after training completes.
                This may increase training time but provide valuable insights.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
 {/* Model Configuration Section */}
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">⚙️</span>
          <span className="font-bold text-xl bg-gradient-to-r from-gray-300 to-gray-100 bg-clip-text text-transparent">
            Classification Model Configuration
          </span>
        </div>
      </div>

      {/* Model Type Selection for Classification */}
      <div>
        <label className="block font-semibold mb-2 text-gray-300">Classification Model</label>
        <select
          className="bg-gray-800 border border-gray-600/50 w-full mb-3 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
          value={modalParams.model_type}
          onChange={(e) => handleParamChange('model_type', e.target.value)}
        >
          <option value="random_forest">Random Forest (Recommended)</option>
          <option value="gradient_boosting">Gradient Boosting</option>
          <option value="logistic_regression">Logistic Regression</option>
          <option value="svc">Support Vector Classification</option>
          <option value="decision_tree">Decision Tree</option>
          {/* <option value="naive_bayes">Naive Bayes</option> */}
          {/* <option value="knn">K-Nearest Neighbors</option> */}
        </select>
      </div>

      {/* Common Parameters */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block font-semibold mb-2 text-gray-300">Test Size</label>
          <input
            type="number"
            step="0.01"
            min="0.1"
            max="0.5"
            className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
            value={modalParams.test_size || 0.2}
            onChange={(e) => handleParamChange('test_size', parseFloat(e.target.value))}
          />
          <div className="text-xs text-gray-500 mt-1">Fraction of data for testing</div>
        </div>
        <div>
          <label className="block font-semibold mb-2 text-gray-300">Random State</label>
          <input
            type="number"
            className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
            value={modalParams.random_state || 42}
            onChange={(e) => handleParamChange('random_state', parseInt(e.target.value))}
          />
          <div className="text-xs text-gray-500 mt-1">For reproducible results</div>
        </div>
      </div>

      {/* Tree-based Models Parameters */}
      {['random_forest', 'gradient_boosting', 'decision_tree'].includes(modalParams.model_type) && (
        <div className="border-t border-gray-700/50 pt-4 mt-4">
          <h5 className="font-semibold text-gray-300 mb-3 text-lg">Tree Model Parameters</h5>
          <div className="grid grid-cols-2 gap-6">
            {/* Only show for ensemble methods */}
            {['random_forest', 'gradient_boosting'].includes(modalParams.model_type) && (
              <div>
                <label className="block font-semibold mb-2 text-gray-300">Number of Estimators</label>
                <input
                  type="number"
                  min="10"
                  max="1000"
                  className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                  value={modalParams.n_estimators || 100}
                  onChange={(e) => handleParamChange('n_estimators', parseInt(e.target.value))}
                />
                <div className="text-xs text-gray-500 mt-1">Number of trees in the ensemble</div>
              </div>
            )}
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Max Depth</label>
              <input
                type="number"
                min="1"
                max="50"
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.max_depth || 10}
                onChange={(e) => handleParamChange('max_depth', parseInt(e.target.value))}
              />
              <div className="text-xs text-gray-500 mt-1">Maximum depth of each tree</div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6 mt-4">
            
            
            
            
            {/* Gradient Boosting specific */}
            {modalParams.model_type === 'gradient_boosting' && (
              <div>
                <label className="block font-semibold mb-2 text-gray-300">Learning Rate</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.001"
                  max="1"
                  className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                  value={modalParams.learning_rate || 0.1}
                  onChange={(e) => handleParamChange('learning_rate', parseFloat(e.target.value))}
                />
                <div className="text-xs text-gray-500 mt-1">Controls the contribution of each tree</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Logistic Regression Parameters */}
      {modalParams.model_type === 'logistic_regression' && (
        <div className="border-t border-gray-700/50 pt-4 mt-4">
          <h5 className="font-semibold text-gray-300 mb-3 text-lg">Logistic Regression Parameters</h5>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block font-semibold mb-2 text-gray-300">C (Regularization Strength)</label>
              <input
                type="number"
                step="0.1"
                min="0.001"
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.C || 1.0}
                onChange={(e) => handleParamChange('C', parseFloat(e.target.value))}
              />
              <div className="text-xs text-gray-500 mt-1">Lower values = more regularization</div>
            </div>
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Max Iterations</label>
              <input
                type="number"
                min="100"
                max="10000"
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.max_iter || 1000}
                onChange={(e) => handleParamChange('max_iter', parseInt(e.target.value))}
              />
              <div className="text-xs text-gray-500 mt-1">Maximum number of iterations</div>
            </div>
          </div>
          
          <div className="mt-4">
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Solver</label>
              <select
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.solver || 'lbfgs'}
                onChange={(e) => handleParamChange('solver', e.target.value)}
              >
                <option value="lbfgs">L-BFGS (Small datasets)</option>
                <option value="liblinear">Liblinear (Small datasets)</option>
                <option value="sag">SAG (Large datasets)</option>
                <option value="saga">SAGA (Large datasets)</option>
              </select>
              <div className="text-xs text-gray-500 mt-1">Algorithm for optimization</div>
            </div>
          </div>
        </div>
      )}

      {/* SVC Parameters */}
      {modalParams.model_type === 'svc' && (
        <div className="border-t border-gray-700/50 pt-4 mt-4">
          <h5 className="font-semibold text-gray-300 mb-3 text-lg">Support Vector Classification Parameters</h5>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Kernel</label>
              <select
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.kernel || 'rbf'}
                onChange={(e) => handleParamChange('kernel', e.target.value)}
              >
                <option value="linear">Linear</option>
                <option value="poly">Polynomial</option>
                <option value="rbf">RBF (Recommended)</option>
                <option value="sigmoid">Sigmoid</option>
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2 text-gray-300">C (Regularization)</label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.C || 1.0}
                onChange={(e) => handleParamChange('C', parseFloat(e.target.value))}
              />
            </div>
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Gamma</label>
              <select
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.gamma || 'scale'}
                onChange={(e) => handleParamChange('gamma', e.target.value)}
              >
                <option value="scale">Scale (Recommended)</option>
                <option value="auto">Auto</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* K-Nearest Neighbors Parameters */}
      {modalParams.model_type === 'knn' && (
        <div className="border-t border-gray-700/50 pt-4 mt-4">
          <h5 className="font-semibold text-gray-300 mb-3 text-lg">K-Nearest Neighbors Parameters</h5>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Number of Neighbors (K)</label>
              <input
                type="number"
                min="1"
                max="50"
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.n_neighbors || 5}
                onChange={(e) => handleParamChange('n_neighbors', parseInt(e.target.value))}
              />
              <div className="text-xs text-gray-500 mt-1">Number of neighbors to consider</div>
            </div>
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Distance Metric</label>
              <select
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.metric || 'minkowski'}
                onChange={(e) => handleParamChange('metric', e.target.value)}
              >
                <option value="minkowski">Minkowski (Default)</option>
                <option value="euclidean">Euclidean</option>
                <option value="manhattan">Manhattan</option>
                <option value="cosine">Cosine</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6 mt-4">
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Weights</label>
              <select
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.weights || 'uniform'}
                onChange={(e) => handleParamChange('weights', e.target.value)}
              >
                <option value="uniform">Uniform</option>
                <option value="distance">Distance-based</option>
              </select>
              <div className="text-xs text-gray-500 mt-1">How to weight the neighbors</div>
            </div>
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Algorithm</label>
              <select
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.algorithm || 'auto'}
                onChange={(e) => handleParamChange('algorithm', e.target.value)}
              >
                <option value="auto">Auto (Recommended)</option>
                <option value="ball_tree">Ball Tree</option>
                <option value="kd_tree">KD Tree</option>
                <option value="brute">Brute Force</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Naive Bayes Parameters */}
      {modalParams.model_type === 'naive_bayes' && (
        <div className="border-t border-gray-700/50 pt-4 mt-4">
          <h5 className="font-semibold text-gray-300 mb-3 text-lg">Naive Bayes Parameters</h5>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Variant</label>
              <select
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.nb_variant || 'gaussian'}
                onChange={(e) => handleParamChange('nb_variant', e.target.value)}
              >
                <option value="gaussian">Gaussian (Continuous features)</option>
                <option value="multinomial">Multinomial (Count features)</option>
                <option value="bernoulli">Bernoulli (Binary features)</option>
              </select>
              <div className="text-xs text-gray-500 mt-1">Choose based on your data type</div>
            </div>
            {modalParams.nb_variant === 'multinomial' && (
              <div>
                <label className="block font-semibold mb-2 text-gray-300">Alpha (Smoothing)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                  value={modalParams.alpha || 1.0}
                  onChange={(e) => handleParamChange('alpha', parseFloat(e.target.value))}
                />
                <div className="text-xs text-gray-500 mt-1">Additive smoothing parameter</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
)}
    
{modalNodeDef.type === 'trainRegression' && (
  <div className="space-y-6 max-w-[1000px] max-h-[500px] overflow-y-auto bg-gray-900 text-white p-6 rounded-lg">
    {/* Dataset Analysis Status - same as before */}
    <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/50 rounded-lg p-4 backdrop-blur-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">📊</span>
          <span className="font-semibold text-gray-100 text-lg">Dataset Analysis</span>
        </div>
        <button
          onClick={async () => {
            const analysis = await analyzeDatasetForAutoML();
            setDatasetAnalysis(analysis);
            if (analysis?.problemType) {
              setDetectedProblemType(analysis.problemType);
              setManualProblemType(analysis.problemType);
            }
          }}
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg transition-all duration-200 font-medium shadow-lg"
        >
          {datasetAnalysis ? 'Re-analyze' : 'Analyze Dataset'}
        </button>
      </div>
      
      {!datasetAnalysis && (
        <div className="mt-3 text-sm text-gray-300">
          Click "Analyze Dataset" to detect problem type and get AI suggestions
        </div>
      )}
    </div>

    {/* Problem Type Detection Results - same as before */}
    {datasetAnalysis?.characteristics?.targetAnalysis && (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-xl bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            🎯 Problem Type Detection
          </h4>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowProblemTypeHelp(!showProblemTypeHelp)}
              className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
            >
              How does this work?
            </button>
            <button
              onClick={() => setOverrideProblemType(!overrideProblemType)}
              className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium ${
                overrideProblemType 
                  ? 'bg-orange-600/20 text-orange-400 hover:bg-orange-600/30 border border-orange-500/30' 
                  : 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-500/30'
              }`}
            >
              {overrideProblemType ? 'Use Auto-Detection' : 'Manual Override'}
            </button>
          </div>
        </div>

        {showProblemTypeHelp && (
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 text-sm text-blue-200 backdrop-blur-sm">
            <strong className="text-blue-100">How Problem Type Detection Works:</strong>
            <ul className="mt-2 space-y-2 text-blue-300">
              <li>• <strong>Classification:</strong> For predicting categories (e.g., spam/not spam, product types)</li>
              <li>• <strong>Regression:</strong> For predicting continuous numbers (e.g., prices, temperatures)</li>
              <li>• The system analyzes your target column's unique values, data types, and patterns</li>
              <li>• High confidence (80%) means the detection is reliable</li>
            </ul>
          </div>
        )}

        <ProblemTypeVisualizer 
          targetAnalysis={datasetAnalysis.characteristics.targetAnalysis}
          targetColumn={datasetAnalysis.characteristics.targetColumn}
        />

        {overrideProblemType && (
          <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-orange-400 text-xl">⚠️</span>
              <span className="font-semibold text-orange-200 text-lg">Manual Problem Type Override</span>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Problem Type:
                </label>
                <select
                  value={manualProblemType}
                  onChange={(e) => setManualProblemType(e.target.value)}
                  className="w-full bg-gray-800 border border-orange-500/30 rounded-lg px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="classification">Classification</option>
                  <option value="regression">Regression</option>
                </select>
              </div>
              <div className="text-sm text-orange-300 bg-orange-900/20 p-3 rounded-lg border border-orange-500/20">
                <strong>Note:</strong> Manual override will change the available models and AI suggestions.
                Make sure this matches your actual prediction goal.
              </div>
            </div>
          </div>
        )}
      </div>
    )}

    {/* AutoML Section - same as before */}
    {datasetAnalysis && (
      <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/30 rounded-lg p-5 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">🤖</span>
            <span className="font-bold text-xl bg-gradient-to-r from-blue-300 to-indigo-300 bg-clip-text text-transparent">
              AI Model Recommendations
            </span>
            <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs rounded-full font-bold shadow-lg">
              SMART
            </span>
          </div>
          <button
            onClick={() => setShowAutoML(!showAutoML)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              showAutoML 
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 shadow-lg' 
                : 'bg-gray-800 text-blue-400 border border-blue-500/30 hover:bg-gray-700'
            }`}
          >
            {showAutoML ? 'Hide AI Suggestions' : 'Get AI Suggestions'}
          </button>
        </div>
        
        <p className="text-blue-300 text-sm mb-3">
          Based on your {overrideProblemType ? manualProblemType : datasetAnalysis.problemType} problem 
          with {datasetAnalysis.characteristics.rowCount} samples and {datasetAnalysis.characteristics.columnCount} features.
        </p>

        {showAutoML && (
          <AutoMLSuggestion
            datasetCharacteristics={datasetAnalysis.characteristics}
            currentProblemType={overrideProblemType ? manualProblemType : datasetAnalysis.problemType}
            onModelSelect={(modelType) => {
              handleParamChange('model_type', modelType);
            }}
            onParametersSelect={(parameters) => {
              Object.entries(parameters).forEach(([key, value]) => {
                handleParamChange(key, value);
              });
            }}
            visible={showAutoML}
          />
        )}
      </div>
    )}

    {/* Hyperparameter Optimization Section - same as before */}
    <div className="bg-gradient-to-r from-purple-900/40 to-violet-900/40 border border-purple-500/30 rounded-lg p-5 backdrop-blur-sm">
      <HyperparameterOptimizer
        modelType={modalParams.model_type}
        isEnabled={enableOptimization}
        onOptimizationToggle={(enabled) => {
          setEnableOptimization(enabled);
          handleParamChange('enable_optimization', enabled);
          if (enabled) {
            handleParamChange('optimization', optimizationConfig);
          } else {
            handleParamChange('optimization', null);
          }
        }}
        onConfigChange={(config) => {
          setOptimizationConfig(config);
          if (enableOptimization) {
            handleParamChange('optimization', config);
          }
        }}
        className="mb-0"
      />
    </div>

    {/* Model Explainability Section - same as before */}
    <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 border border-green-500/30 rounded-lg p-5 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">🔍</span>
          <span className="font-bold text-xl bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">
            Model Explainability
          </span>
          <span className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs rounded-full font-bold shadow-lg">
            INSIGHTS
          </span>
        </div>
        
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enableExplanations}
            onChange={(e) => {
              setEnableExplanations(e.target.checked);
              handleParamChange('enable_explanations', e.target.checked);
            }}
            className="w-5 h-5 text-green-500 bg-gray-800 border-green-500/30 rounded focus:ring-green-500 focus:ring-2"
          />
          <span className="text-sm font-medium text-green-300">
            Generate Explanations
          </span>
        </label>
      </div>
      
      <div className="text-green-300 text-sm">
        <p className="mb-3">
          Understand your model's decisions with SHAP and LIME explanations:
        </p>
        <ul className="space-y-2 text-green-400">
          <li>• <strong>Feature Importance:</strong> Which features matter most</li>
          <li>• <strong>SHAP Values:</strong> How each feature impacts predictions</li>
          <li>• <strong>LIME Analysis:</strong> Local explanations for individual predictions</li>
        </ul>
        
        {enableExplanations && (
          <div className="mt-4 p-3 bg-green-900/30 rounded-lg border border-green-500/30">
            <div className="flex items-center space-x-2">
              <span className="text-green-400 text-lg">✅</span>
              <span className="text-sm text-green-200">
                <strong>Enabled:</strong> Explanations will be generated after training completes.
                This may increase training time but provide valuable insights.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Model Configuration Section */}
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">⚙️</span>
          <span className="font-bold text-xl bg-gradient-to-r from-gray-300 to-gray-100 bg-clip-text text-transparent">
            Regression Model Configuration
          </span>
        </div>
      </div>

      {/* Model Type Selection for Regression */}
      <div>
        <label className="block font-semibold mb-2 text-gray-300">Regression Model</label>
        <select
          className="bg-gray-800 border border-gray-600/50 w-full mb-3 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
          value={modalParams.model_type}
          onChange={(e) => handleParamChange('model_type', e.target.value)}
        >
          <option value="linear_regression">Linear Regression</option>
          <option value="ridge">Ridge Regression (Recommended)</option>
          <option value="lasso">Lasso Regression</option>
          <option value="elastic_net">Elastic Net</option>
          <option value="svr">Support Vector Regression</option>
          <option value="random_forest_regressor">Random Forest Regressor</option>
          <option value="gradient_boosting_regressor">Gradient Boosting Regressor</option>
        </select>
      </div>

      {/* Common Parameters */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block font-semibold mb-2 text-gray-300">Test Size</label>
          <input
            type="number"
            step="0.01"
            min="0.1"
            max="0.5"
            className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
            value={modalParams.test_size || 0.2}
            onChange={(e) => handleParamChange('test_size', parseFloat(e.target.value))}
          />
          <div className="text-xs text-gray-500 mt-1">Fraction of data for testing</div>
        </div>
        <div>
          <label className="block font-semibold mb-2 text-gray-300">Random State</label>
          <input
            type="number"
            className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
            value={modalParams.random_state || 42}
            onChange={(e) => handleParamChange('random_state', parseInt(e.target.value))}
          />
          <div className="text-xs text-gray-500 mt-1">For reproducible results</div>
        </div>
      </div>

      {/* Linear Model Parameters */}
      {['ridge', 'lasso', 'elastic_net'].includes(modalParams.model_type) && (
        <div className="border-t border-gray-700/50 pt-4 mt-4">
          <h5 className="font-semibold text-gray-300 mb-3 text-lg">Regularization Parameters</h5>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Alpha (Regularization Strength)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.alpha || 1.0}
                onChange={(e) => handleParamChange('alpha', parseFloat(e.target.value))}
              />
              <div className="text-xs text-gray-500 mt-1">Higher values = more regularization</div>
            </div>
            {modalParams.model_type === 'elastic_net' && (
              <div>
                <label className="block font-semibold mb-2 text-gray-300">L1 Ratio</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                  value={modalParams.l1_ratio || 0.5}
                  onChange={(e) => handleParamChange('l1_ratio', parseFloat(e.target.value))}
                />
                <div className="text-xs text-gray-500 mt-1">0 = Ridge, 1 = Lasso, 0.5 = Equal mix</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tree-based Models Parameters */}
      {['random_forest_regressor', 'gradient_boosting_regressor'].includes(modalParams.model_type) && (
        <div className="border-t border-gray-700/50 pt-4 mt-4">
          <h5 className="font-semibold text-gray-300 mb-3 text-lg">Tree Model Parameters</h5>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Number of Estimators</label>
              <input
                type="number"
                min="10"
                max="1000"
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.n_estimators || 100}
                onChange={(e) => handleParamChange('n_estimators', parseInt(e.target.value))}
              />
              <div className="text-xs text-gray-500 mt-1">Number of trees in the ensemble</div>
            </div>
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Max Depth</label>
              <input
                type="number"
                min="1"
                max="50"
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.max_depth || 10}
                onChange={(e) => handleParamChange('max_depth', parseInt(e.target.value))}
              />
              <div className="text-xs text-gray-500 mt-1">Maximum depth of each tree</div>
            </div>
          </div>
          
          {modalParams.model_type === 'gradient_boosting_regressor' && (
            <div className="grid grid-cols-2 gap-6 mt-4">
              <div>
                <label className="block font-semibold mb-2 text-gray-300">Learning Rate</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.001"
                  max="1"
                  className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                  value={modalParams.learning_rate || 0.1}
                  onChange={(e) => handleParamChange('learning_rate', parseFloat(e.target.value))}
                />
                <div className="text-xs text-gray-500 mt-1">Controls the contribution of each tree</div>
              </div>
              
            </div>
          )}
        </div>
      )}

      {/* SVR Parameters */}
      {modalParams.model_type === 'svr' && (
        <div className="border-t border-gray-700/50 pt-4 mt-4">
          <h5 className="font-semibold text-gray-300 mb-3 text-lg">Support Vector Regression Parameters</h5>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Kernel</label>
              <select
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.kernel || 'rbf'}
                onChange={(e) => handleParamChange('kernel', e.target.value)}
              >
                <option value="linear">Linear</option>
                <option value="poly">Polynomial</option>
                <option value="rbf">RBF (Recommended)</option>
                <option value="sigmoid">Sigmoid</option>
              </select>
            </div>
            <div>
              <label className="block font-semibold mb-2 text-gray-300">C (Regularization)</label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.C || 1.0}
                onChange={(e) => handleParamChange('C', parseFloat(e.target.value))}
              />
            </div>
            <div>
              <label className="block font-semibold mb-2 text-gray-300">Epsilon</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                value={modalParams.epsilon || 0.1}
                onChange={(e) => handleParamChange('epsilon', parseFloat(e.target.value))}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
)}
{modalNodeDef.type === 'trainTimeSeries' && (
  <div className="space-y-6 max-w-[1000px] max-h-[500px] overflow-y-auto bg-gray-900 text-white p-6 rounded-lg">
    {/* Dataset Analysis Status */}
    <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/50 rounded-lg p-4 backdrop-blur-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">📊</span>
          <span className="font-semibold text-gray-100 text-lg">Dataset Analysis</span>
        </div>
        <button
          onClick={async () => {
            const analysis = await analyzeDatasetForAutoML();
            setDatasetAnalysis(analysis);
            if (analysis?.problemType) {
              setDetectedProblemType(analysis.problemType);
              setManualProblemType(analysis.problemType);
            }
          }}
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg transition-all duration-200 font-medium shadow-lg"
        >
          {datasetAnalysis ? 'Re-analyze' : 'Analyze Dataset'}
        </button>
      </div>
      
      {!datasetAnalysis && (
        <div className="mt-3 text-sm text-gray-300">
          Click "Analyze Dataset" to detect problem type and get AI suggestions
        </div>
      )}
    </div>

    {/* Problem Type Detection Results */}
    {datasetAnalysis?.characteristics?.targetAnalysis && (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-xl bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            🎯 Problem Type Detection
          </h4>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowProblemTypeHelp(!showProblemTypeHelp)}
              className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
            >
              How does this work?
            </button>
            <button
              onClick={() => setOverrideProblemType(!overrideProblemType)}
              className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium ${
                overrideProblemType 
                  ? 'bg-orange-600/20 text-orange-400 hover:bg-orange-600/30 border border-orange-500/30' 
                  : 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-500/30'
              }`}
            >
              {overrideProblemType ? 'Use Auto-Detection' : 'Manual Override'}
            </button>
          </div>
        </div>

        {/* Help Text */}
        {showProblemTypeHelp && (
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 text-sm text-blue-200 backdrop-blur-sm">
            <strong className="text-blue-100">How Problem Type Detection Works:</strong>
            <ul className="mt-2 space-y-2 text-blue-300">
              <li>• <strong>Classification:</strong> For predicting categories (e.g., spam/not spam, product types)</li>
              <li>• <strong>Regression:</strong> For predicting continuous numbers (e.g., prices, temperatures)</li>
              <li>• The system analyzes your target column's unique values, data types, and patterns</li>
              <li>• High confidence (80%) means the detection is reliable</li>
            </ul>
          </div>
        )}

        <ProblemTypeVisualizer 
          targetAnalysis={datasetAnalysis.characteristics.targetAnalysis}
          targetColumn={datasetAnalysis.characteristics.targetColumn}
        />

        {/* Manual Override Section */}
        {overrideProblemType && (
          <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-orange-400 text-xl">⚠️</span>
              <span className="font-semibold text-orange-200 text-lg">Manual Problem Type Override</span>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Problem Type:
                </label>
                <select
                  value={manualProblemType}
                  onChange={(e) => setManualProblemType(e.target.value)}
                  className="w-full bg-gray-800 border border-orange-500/30 rounded-lg px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="classification">Classification</option>
                  <option value="regression">Regression</option>
                </select>
              </div>
              <div className="text-sm text-orange-300 bg-orange-900/20 p-3 rounded-lg border border-orange-500/20">
                <strong>Note:</strong> Manual override will change the available models and AI suggestions.
                Make sure this matches your actual prediction goal.
              </div>
            </div>
          </div>
        )}
      </div>
    )}

    {/* AutoML Section */}
    {datasetAnalysis && (
      <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/30 rounded-lg p-5 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">🤖</span>
            <span className="font-bold text-xl bg-gradient-to-r from-blue-300 to-indigo-300 bg-clip-text text-transparent">
              AI Model Recommendations
            </span>
            <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs rounded-full font-bold shadow-lg">
              SMART
            </span>
          </div>
          <button
            onClick={() => setShowAutoML(!showAutoML)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              showAutoML 
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 shadow-lg' 
                : 'bg-gray-800 text-blue-400 border border-blue-500/30 hover:bg-gray-700'
            }`}
          >
            {showAutoML ? 'Hide AI Suggestions' : 'Get AI Suggestions'}
          </button>
        </div>
        
        <p className="text-blue-300 text-sm mb-3">
          Based on your {overrideProblemType ? manualProblemType : datasetAnalysis.problemType} problem 
          with {datasetAnalysis.characteristics.rowCount} samples and {datasetAnalysis.characteristics.columnCount} features.
        </p>

        {showAutoML && (
          <AutoMLSuggestion
            datasetCharacteristics={datasetAnalysis.characteristics}
            currentProblemType={overrideProblemType ? manualProblemType : datasetAnalysis.problemType}
            onModelSelect={(modelType) => {
              handleParamChange('model_type', modelType);
            }}
            onParametersSelect={(parameters) => {
              Object.entries(parameters).forEach(([key, value]) => {
                handleParamChange(key, value);
              });
            }}
            visible={showAutoML}
          />
        )}
      </div>
    )}

    {/* Hyperparameter Optimization Section */}
    <div className="bg-gradient-to-r from-purple-900/40 to-violet-900/40 border border-purple-500/30 rounded-lg p-5 backdrop-blur-sm">
      <HyperparameterOptimizer
        modelType={modalParams.model_type}
        isEnabled={enableOptimization}
        onOptimizationToggle={(enabled) => {
          setEnableOptimization(enabled);
          handleParamChange('enable_optimization', enabled);
          if (enabled) {
            handleParamChange('optimization', optimizationConfig);
          } else {
            handleParamChange('optimization', null);
          }
        }}
        onConfigChange={(config) => {
          setOptimizationConfig(config);
          if (enableOptimization) {
            handleParamChange('optimization', config);
          }
        }}
        className="mb-0"
      />
    </div>

    {/* Model Explainability Section */}
    <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 border border-green-500/30 rounded-lg p-5 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">🔍</span>
          <span className="font-bold text-xl bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">
            Model Explainability
          </span>
          <span className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs rounded-full font-bold shadow-lg">
            INSIGHTS
          </span>
        </div>
        
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enableExplanations}
            onChange={(e) => {
              console.log('🔍 EXPLAINABILITY CHECKBOX CHANGED:', e.target.checked);
              console.log('🔍 Current modalParams before change:', modalParams);
              setEnableExplanations(e.target.checked);
              handleParamChange('enable_explanations', e.target.checked);
              console.log('🔍 EnableExplanations state set to:', e.target.checked);
            }}
            className="w-5 h-5 text-green-500 bg-gray-800 border-green-500/30 rounded focus:ring-green-500 focus:ring-2"
          />
          <span className="text-sm font-medium text-green-300">
            Generate Explanations
          </span>
        </label>
      </div>
      
      {/* DEBUG: Show current states - Styled for dark theme */}
      <div className="text-xs text-gray-500 mb-3 bg-gray-800/50 p-3 rounded-lg border border-gray-600/30">
        <strong className="text-gray-400">Debug Info:</strong><br/>
        <span className="text-gray-400">enableExplanations state:</span> <span className="text-green-400">{enableExplanations.toString()}</span><br/>
        <span className="text-gray-400">modalParams.enable_explanations:</span> <span className="text-green-400">{modalParams.enable_explanations?.toString() || 'undefined'}</span><br/>
        <span className="text-gray-400">modalParams keys:</span> <span className="text-blue-400">{Object.keys(modalParams).join(', ')}</span>
      </div>
      
      <div className="text-green-300 text-sm">
        <p className="mb-3">
          Understand your model's decisions with SHAP and LIME explanations:
        </p>
        <ul className="space-y-2 text-green-400">
          <li>• <strong>Feature Importance:</strong> Which features matter most</li>
          <li>• <strong>SHAP Values:</strong> How each feature impacts predictions</li>
          <li>• <strong>LIME Analysis:</strong> Local explanations for individual predictions</li>
        </ul>
        
        {enableExplanations && (
          <div className="mt-4 p-3 bg-green-900/30 rounded-lg border border-green-500/30">
            <div className="flex items-center space-x-2">
              <span className="text-green-400 text-lg">✅</span>
              <span className="text-sm text-green-200">
                <strong>Enabled:</strong> Explanations will be generated after training completes.
                This may increase training time but provide valuable insights.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
    {/* Time Series Model Selection */}
    <div className="space-y-4">
      <div>
        <label className="block font-semibold mb-2 text-gray-300">Time Series Model</label>
        <select
          className="bg-gray-800 border border-gray-600/50 w-full mb-3 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
          value={modalParams.model_type}
          onChange={(e) => handleParamChange('model_type', e.target.value)}
        >
          <option value="arima">ARIMA</option>
          <option value="sarima">SARIMA (Seasonal ARIMA)</option>
          <option value="prophet">Facebook Prophet (Recommended)</option>
        </select>
      </div>
      
      {/* ARIMA/SARIMA Parameters */}
      {['arima', 'sarima'].includes(modalParams.model_type) && (
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block font-semibold mb-2 text-gray-300">p (AR order)</label>
            <input
              type="number"
              min="0"
              max="10"
              className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg"
              value={modalParams.p || 1}
              onChange={(e) => handleParamChange('p', parseInt(e.target.value))}
            />
          </div>
          <div>
            <label className="block font-semibold mb-2 text-gray-300">d (Differences)</label>
            <input
              type="number"
              min="0"
              max="3"
              className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg"
              value={modalParams.d || 1}
              onChange={(e) => handleParamChange('d', parseInt(e.target.value))}
            />
          </div>
          <div>
            <label className="block font-semibold mb-2 text-gray-300">q (MA order)</label>
            <input
              type="number"
              min="0"
              max="10"
              className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg"
              value={modalParams.q || 1}
              onChange={(e) => handleParamChange('q', parseInt(e.target.value))}
            />
          </div>
        </div>
      )}
      
      {/* Prophet Parameters */}
      {modalParams.model_type === 'prophet' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={modalParams.yearly_seasonality ?? true}
                onChange={(e) => handleParamChange('yearly_seasonality', e.target.checked)}
                className="mr-2"
              />
              <span>Yearly Seasonality</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={modalParams.weekly_seasonality ?? true}
                onChange={(e) => handleParamChange('weekly_seasonality', e.target.checked)}
                className="mr-2"
              />
              <span>Weekly Seasonality</span>
            </label>
          </div>
        </div>
      )}
    </div>
  </div>
)}
{modalNodeDef.type === 'trainDeepLearning' && (
  <div className="space-y-6 max-w-[1000px] max-h-[500px] overflow-y-auto bg-gray-900 text-white p-6 rounded-lg">
    {/* Dataset Analysis Status */}
    <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/50 rounded-lg p-4 backdrop-blur-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">📊</span>
          <span className="font-semibold text-gray-100 text-lg">Dataset Analysis</span>
        </div>
        <button
          onClick={async () => {
            const analysis = await analyzeDatasetForAutoML();
            setDatasetAnalysis(analysis);
            if (analysis?.problemType) {
              setDetectedProblemType(analysis.problemType);
              setManualProblemType(analysis.problemType);
            }
          }}
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg transition-all duration-200 font-medium shadow-lg"
        >
          {datasetAnalysis ? 'Re-analyze' : 'Analyze Dataset'}
        </button>
      </div>
      
      {!datasetAnalysis && (
        <div className="mt-3 text-sm text-gray-300">
          Click "Analyze Dataset" to detect problem type and get AI suggestions
        </div>
      )}
    </div>

    {/* Problem Type Detection Results */}
    {datasetAnalysis?.characteristics?.targetAnalysis && (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-xl bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            🎯 Problem Type Detection
          </h4>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowProblemTypeHelp(!showProblemTypeHelp)}
              className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
            >
              How does this work?
            </button>
            <button
              onClick={() => setOverrideProblemType(!overrideProblemType)}
              className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium ${
                overrideProblemType 
                  ? 'bg-orange-600/20 text-orange-400 hover:bg-orange-600/30 border border-orange-500/30' 
                  : 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-500/30'
              }`}
            >
              {overrideProblemType ? 'Use Auto-Detection' : 'Manual Override'}
            </button>
          </div>
        </div>

        {/* Help Text */}
        {showProblemTypeHelp && (
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 text-sm text-blue-200 backdrop-blur-sm">
            <strong className="text-blue-100">How Problem Type Detection Works:</strong>
            <ul className="mt-2 space-y-2 text-blue-300">
              <li>• <strong>Classification:</strong> For predicting categories (e.g., spam/not spam, product types)</li>
              <li>• <strong>Regression:</strong> For predicting continuous numbers (e.g., prices, temperatures)</li>
              <li>• The system analyzes your target column's unique values, data types, and patterns</li>
              <li>• High confidence (80%) means the detection is reliable</li>
            </ul>
          </div>
        )}

        <ProblemTypeVisualizer 
          targetAnalysis={datasetAnalysis.characteristics.targetAnalysis}
          targetColumn={datasetAnalysis.characteristics.targetColumn}
        />

        {/* Manual Override Section */}
        {overrideProblemType && (
          <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-orange-400 text-xl">⚠️</span>
              <span className="font-semibold text-orange-200 text-lg">Manual Problem Type Override</span>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Problem Type:
                </label>
                <select
                  value={manualProblemType}
                  onChange={(e) => setManualProblemType(e.target.value)}
                  className="w-full bg-gray-800 border border-orange-500/30 rounded-lg px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="classification">Classification</option>
                  <option value="regression">Regression</option>
                </select>
              </div>
              <div className="text-sm text-orange-300 bg-orange-900/20 p-3 rounded-lg border border-orange-500/20">
                <strong>Note:</strong> Manual override will change the available models and AI suggestions.
                Make sure this matches your actual prediction goal.
              </div>
            </div>
          </div>
        )}
      </div>
    )}

    {/* AutoML Section */}
    {datasetAnalysis && (
      <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/30 rounded-lg p-5 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">🤖</span>
            <span className="font-bold text-xl bg-gradient-to-r from-blue-300 to-indigo-300 bg-clip-text text-transparent">
              AI Model Recommendations
            </span>
            <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs rounded-full font-bold shadow-lg">
              SMART
            </span>
          </div>
          <button
            onClick={() => setShowAutoML(!showAutoML)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              showAutoML 
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 shadow-lg' 
                : 'bg-gray-800 text-blue-400 border border-blue-500/30 hover:bg-gray-700'
            }`}
          >
            {showAutoML ? 'Hide AI Suggestions' : 'Get AI Suggestions'}
          </button>
        </div>
        
        <p className="text-blue-300 text-sm mb-3">
          Based on your {overrideProblemType ? manualProblemType : datasetAnalysis.problemType} problem 
          with {datasetAnalysis.characteristics.rowCount} samples and {datasetAnalysis.characteristics.columnCount} features.
        </p>

        {showAutoML && (
          <AutoMLSuggestion
            datasetCharacteristics={datasetAnalysis.characteristics}
            currentProblemType={overrideProblemType ? manualProblemType : datasetAnalysis.problemType}
            onModelSelect={(modelType) => {
              handleParamChange('model_type', modelType);
            }}
            onParametersSelect={(parameters) => {
              Object.entries(parameters).forEach(([key, value]) => {
                handleParamChange(key, value);
              });
            }}
            visible={showAutoML}
          />
        )}
      </div>
    )}

    {/* Hyperparameter Optimization Section */}
    <div className="bg-gradient-to-r from-purple-900/40 to-violet-900/40 border border-purple-500/30 rounded-lg p-5 backdrop-blur-sm">
      <HyperparameterOptimizer
        modelType={modalParams.model_type}
        isEnabled={enableOptimization}
        onOptimizationToggle={(enabled) => {
          setEnableOptimization(enabled);
          handleParamChange('enable_optimization', enabled);
          if (enabled) {
            handleParamChange('optimization', optimizationConfig);
          } else {
            handleParamChange('optimization', null);
          }
        }}
        onConfigChange={(config) => {
          setOptimizationConfig(config);
          if (enableOptimization) {
            handleParamChange('optimization', config);
          }
        }}
        className="mb-0"
      />
    </div>

    {/* Model Explainability Section */}
    <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 border border-green-500/30 rounded-lg p-5 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">🔍</span>
          <span className="font-bold text-xl bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">
            Model Explainability
          </span>
          <span className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs rounded-full font-bold shadow-lg">
            INSIGHTS
          </span>
        </div>
        
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enableExplanations}
            onChange={(e) => {
              console.log('🔍 EXPLAINABILITY CHECKBOX CHANGED:', e.target.checked);
              console.log('🔍 Current modalParams before change:', modalParams);
              setEnableExplanations(e.target.checked);
              handleParamChange('enable_explanations', e.target.checked);
              console.log('🔍 EnableExplanations state set to:', e.target.checked);
            }}
            className="w-5 h-5 text-green-500 bg-gray-800 border-green-500/30 rounded focus:ring-green-500 focus:ring-2"
          />
          <span className="text-sm font-medium text-green-300">
            Generate Explanations
          </span>
        </label>
      </div>
      
      {/* DEBUG: Show current states - Styled for dark theme */}
      <div className="text-xs text-gray-500 mb-3 bg-gray-800/50 p-3 rounded-lg border border-gray-600/30">
        <strong className="text-gray-400">Debug Info:</strong><br/>
        <span className="text-gray-400">enableExplanations state:</span> <span className="text-green-400">{enableExplanations.toString()}</span><br/>
        <span className="text-gray-400">modalParams.enable_explanations:</span> <span className="text-green-400">{modalParams.enable_explanations?.toString() || 'undefined'}</span><br/>
        <span className="text-gray-400">modalParams keys:</span> <span className="text-blue-400">{Object.keys(modalParams).join(', ')}</span>
      </div>
      
      <div className="text-green-300 text-sm">
        <p className="mb-3">
          Understand your model's decisions with SHAP and LIME explanations:
        </p>
        <ul className="space-y-2 text-green-400">
          <li>• <strong>Feature Importance:</strong> Which features matter most</li>
          <li>• <strong>SHAP Values:</strong> How each feature impacts predictions</li>
          <li>• <strong>LIME Analysis:</strong> Local explanations for individual predictions</li>
        </ul>
        
        {enableExplanations && (
          <div className="mt-4 p-3 bg-green-900/30 rounded-lg border border-green-500/30">
            <div className="flex items-center space-x-2">
              <span className="text-green-400 text-lg">✅</span>
              <span className="text-sm text-green-200">
                <strong>Enabled:</strong> Explanations will be generated after training completes.
                This may increase training time but provide valuable insights.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
    {/* Deep Learning Model Selection */}
    <div className="space-y-4">
      <div>
        <label className="block font-semibold mb-2 text-gray-300">Deep Learning Model</label>
        <select
          className="bg-gray-800 border border-gray-600/50 w-full mb-3 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
          value={modalParams.model_type}
          onChange={(e) => handleParamChange('model_type', e.target.value)}
        >
          <option value="mlp">Multi-Layer Perceptron (MLP)</option>
          <option value="lstm">LSTM Network</option>
        </select>
      </div>
      
      {/* MLP Parameters */}
      {modalParams.model_type === 'mlp' && (
        <div className="space-y-4">
          <div>
            <label className="block font-semibold mb-2 text-gray-300">Hidden Layers</label>
            <input
              type="text"
              placeholder="e.g., 100,50 for two layers"
              className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg"
              value={Array.isArray(modalParams.hidden_layers) ? modalParams.hidden_layers.join(',') : '100,50'}
              onChange={(e) => {
                const layers = e.target.value.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
                handleParamChange('hidden_layers', layers);
              }}
            />
          </div>
        </div>
      )}
      
      {/* LSTM Parameters */}
      {modalParams.model_type === 'lstm' && (
        <div className="space-y-4">
          <div>
            <label className="block font-semibold mb-2 text-gray-300">Sequence Length</label>
            <input
              type="number"
              min="5"
              max="100"
              className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg"
              value={modalParams.sequence_length || 10}
              onChange={(e) => handleParamChange('sequence_length', parseInt(e.target.value))}
            />
          </div>
        </div>
      )}
    </div>
  </div>
)}

{modalNodeDef.type === 'trainClustering' && (
  <div className="space-y-6 max-w-[1000px] max-h-[500px] overflow-y-auto bg-gray-900 text-white p-6 rounded-lg">
    {/* Dataset Analysis Status */}
    <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/50 rounded-lg p-4 backdrop-blur-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">📊</span>
          <span className="font-semibold text-gray-100 text-lg">Dataset Analysis</span>
        </div>
        <button
          onClick={async () => {
            const analysis = await analyzeDatasetForAutoML();
            setDatasetAnalysis(analysis);
            if (analysis?.problemType) {
              setDetectedProblemType(analysis.problemType);
              setManualProblemType(analysis.problemType);
            }
          }}
          className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg transition-all duration-200 font-medium shadow-lg"
        >
          {datasetAnalysis ? 'Re-analyze' : 'Analyze Dataset'}
        </button>
      </div>
      
      {!datasetAnalysis && (
        <div className="mt-3 text-sm text-gray-300">
          Click "Analyze Dataset" to detect problem type and get AI suggestions
        </div>
      )}
    </div>

    {/* Problem Type Detection Results */}
    {datasetAnalysis?.characteristics?.targetAnalysis && (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-xl bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            🎯 Problem Type Detection
          </h4>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowProblemTypeHelp(!showProblemTypeHelp)}
              className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
            >
              How does this work?
            </button>
            <button
              onClick={() => setOverrideProblemType(!overrideProblemType)}
              className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium ${
                overrideProblemType 
                  ? 'bg-orange-600/20 text-orange-400 hover:bg-orange-600/30 border border-orange-500/30' 
                  : 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-500/30'
              }`}
            >
              {overrideProblemType ? 'Use Auto-Detection' : 'Manual Override'}
            </button>
          </div>
        </div>

        {/* Help Text */}
        {showProblemTypeHelp && (
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 text-sm text-blue-200 backdrop-blur-sm">
            <strong className="text-blue-100">How Problem Type Detection Works:</strong>
            <ul className="mt-2 space-y-2 text-blue-300">
              <li>• <strong>Classification:</strong> For predicting categories (e.g., spam/not spam, product types)</li>
              <li>• <strong>Regression:</strong> For predicting continuous numbers (e.g., prices, temperatures)</li>
              <li>• The system analyzes your target column's unique values, data types, and patterns</li>
              <li>• High confidence (80%) means the detection is reliable</li>
            </ul>
          </div>
        )}

        <ProblemTypeVisualizer 
          targetAnalysis={datasetAnalysis.characteristics.targetAnalysis}
          targetColumn={datasetAnalysis.characteristics.targetColumn}
        />

        {/* Manual Override Section */}
        {overrideProblemType && (
          <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-orange-400 text-xl">⚠️</span>
              <span className="font-semibold text-orange-200 text-lg">Manual Problem Type Override</span>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Problem Type:
                </label>
                <select
                  value={manualProblemType}
                  onChange={(e) => setManualProblemType(e.target.value)}
                  className="w-full bg-gray-800 border border-orange-500/30 rounded-lg px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="classification">Classification</option>
                  <option value="regression">Regression</option>
                </select>
              </div>
              <div className="text-sm text-orange-300 bg-orange-900/20 p-3 rounded-lg border border-orange-500/20">
                <strong>Note:</strong> Manual override will change the available models and AI suggestions.
                Make sure this matches your actual prediction goal.
              </div>
            </div>
          </div>
        )}
      </div>
    )}

    {/* AutoML Section */}
    {datasetAnalysis && (
      <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/30 rounded-lg p-5 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">🤖</span>
            <span className="font-bold text-xl bg-gradient-to-r from-blue-300 to-indigo-300 bg-clip-text text-transparent">
              AI Model Recommendations
            </span>
            <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs rounded-full font-bold shadow-lg">
              SMART
            </span>
          </div>
          <button
            onClick={() => setShowAutoML(!showAutoML)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              showAutoML 
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 shadow-lg' 
                : 'bg-gray-800 text-blue-400 border border-blue-500/30 hover:bg-gray-700'
            }`}
          >
            {showAutoML ? 'Hide AI Suggestions' : 'Get AI Suggestions'}
          </button>
        </div>
        
        <p className="text-blue-300 text-sm mb-3">
          Based on your {overrideProblemType ? manualProblemType : datasetAnalysis.problemType} problem 
          with {datasetAnalysis.characteristics.rowCount} samples and {datasetAnalysis.characteristics.columnCount} features.
        </p>

        {showAutoML && (
          <AutoMLSuggestion
            datasetCharacteristics={datasetAnalysis.characteristics}
            currentProblemType={overrideProblemType ? manualProblemType : datasetAnalysis.problemType}
            onModelSelect={(modelType) => {
              handleParamChange('model_type', modelType);
            }}
            onParametersSelect={(parameters) => {
              Object.entries(parameters).forEach(([key, value]) => {
                handleParamChange(key, value);
              });
            }}
            visible={showAutoML}
          />
        )}
      </div>
    )}

    {/* Hyperparameter Optimization Section */}
    <div className="bg-gradient-to-r from-purple-900/40 to-violet-900/40 border border-purple-500/30 rounded-lg p-5 backdrop-blur-sm">
      <HyperparameterOptimizer
        modelType={modalParams.model_type}
        isEnabled={enableOptimization}
        onOptimizationToggle={(enabled) => {
          setEnableOptimization(enabled);
          handleParamChange('enable_optimization', enabled);
          if (enabled) {
            handleParamChange('optimization', optimizationConfig);
          } else {
            handleParamChange('optimization', null);
          }
        }}
        onConfigChange={(config) => {
          setOptimizationConfig(config);
          if (enableOptimization) {
            handleParamChange('optimization', config);
          }
        }}
        className="mb-0"
      />
    </div>

    {/* Model Explainability Section */}
    <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 border border-green-500/30 rounded-lg p-5 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">🔍</span>
          <span className="font-bold text-xl bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent">
            Model Explainability
          </span>
          <span className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs rounded-full font-bold shadow-lg">
            INSIGHTS
          </span>
        </div>
        
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enableExplanations}
            onChange={(e) => {
              console.log('🔍 EXPLAINABILITY CHECKBOX CHANGED:', e.target.checked);
              console.log('🔍 Current modalParams before change:', modalParams);
              setEnableExplanations(e.target.checked);
              handleParamChange('enable_explanations', e.target.checked);
              console.log('🔍 EnableExplanations state set to:', e.target.checked);
            }}
            className="w-5 h-5 text-green-500 bg-gray-800 border-green-500/30 rounded focus:ring-green-500 focus:ring-2"
          />
          <span className="text-sm font-medium text-green-300">
            Generate Explanations
          </span>
        </label>
      </div>
      
      {/* DEBUG: Show current states - Styled for dark theme */}
      <div className="text-xs text-gray-500 mb-3 bg-gray-800/50 p-3 rounded-lg border border-gray-600/30">
        <strong className="text-gray-400">Debug Info:</strong><br/>
        <span className="text-gray-400">enableExplanations state:</span> <span className="text-green-400">{enableExplanations.toString()}</span><br/>
        <span className="text-gray-400">modalParams.enable_explanations:</span> <span className="text-green-400">{modalParams.enable_explanations?.toString() || 'undefined'}</span><br/>
        <span className="text-gray-400">modalParams keys:</span> <span className="text-blue-400">{Object.keys(modalParams).join(', ')}</span>
      </div>
      
      <div className="text-green-300 text-sm">
        <p className="mb-3">
          Understand your model's decisions with SHAP and LIME explanations:
        </p>
        <ul className="space-y-2 text-green-400">
          <li>• <strong>Feature Importance:</strong> Which features matter most</li>
          <li>• <strong>SHAP Values:</strong> How each feature impacts predictions</li>
          <li>• <strong>LIME Analysis:</strong> Local explanations for individual predictions</li>
        </ul>
        
        {enableExplanations && (
          <div className="mt-4 p-3 bg-green-900/30 rounded-lg border border-green-500/30">
            <div className="flex items-center space-x-2">
              <span className="text-green-400 text-lg">✅</span>
              <span className="text-sm text-green-200">
                <strong>Enabled:</strong> Explanations will be generated after training completes.
                This may increase training time but provide valuable insights.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
    {/* Clustering Model Selection */}
    <div className="space-y-4">
      <div>
        <label className="block font-semibold mb-2 text-gray-300">Clustering Algorithm</label>
        <select
          className="bg-gray-800 border border-gray-600/50 w-full mb-3 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
          value={modalParams.model_type}
          onChange={(e) => handleParamChange('model_type', e.target.value)}
        >
          <option value="kmeans">K-Means Clustering (Recommended)</option>
          <option value="dbscan">DBSCAN</option>
          <option value="hierarchical">Hierarchical Clustering</option>
        </select>
      </div>
      
      {/* K-Means Parameters */}
      {modalParams.model_type === 'kmeans' && (
        <div>
          <label className="block font-semibold mb-2 text-gray-300">Number of Clusters</label>
          <input
            type="number"
            min="2"
            max="20"
            className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg"
            value={modalParams.n_clusters || 3}
            onChange={(e) => handleParamChange('n_clusters', parseInt(e.target.value))}
          />
        </div>
      )}
      
      {/* DBSCAN Parameters */}
      {modalParams.model_type === 'dbscan' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold mb-2 text-gray-300">Epsilon</label>
            <input
              type="number"
              step="0.1"
              className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg"
              value={modalParams.eps || 0.5}
              onChange={(e) => handleParamChange('eps', parseFloat(e.target.value))}
            />
          </div>
          <div>
            <label className="block font-semibold mb-2 text-gray-300">Min Samples</label>
            <input
              type="number"
              min="2"
              className="bg-gray-800 border border-gray-600/50 w-full p-3 rounded-lg"
              value={modalParams.min_samples || 5}
              onChange={(e) => handleParamChange('min_samples', parseInt(e.target.value))}
            />
          </div>
        </div>
      )}
    </div>
  </div>
)}
 {modalNodeDef.type === 'trainCNN' && (
  <div className="space-y-4 max-h-96 overflow-y-auto">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
      <p className="text-sm text-blue-800">
        <strong>Note:</strong> CNN training requires an image dataset organized in folders by class.
      </p>
    </div>

    <div>
      <label className="block font-semibold mb-1">Architecture</label>
      <select
        className="border w-full text-black mb-2 p-1"
        value={modalParams.architecture || 'resnet50'}
        onChange={(e) => handleParamChange('architecture', e.target.value)}
      >
        <option value="resnet50">ResNet50 (Recommended)</option>
        <option value="vgg16">VGG16</option>
        <option value="custom">Custom CNN</option>
      </select>
      <p className="text-xs text-gray-500">ResNet50 offers best balance of accuracy and speed</p>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block font-semibold mb-1">Image Size</label>
        <select
          className="border w-full text-black p-1"
          value={modalParams.img_size || 224}
          onChange={(e) => handleParamChange('img_size', parseInt(e.target.value))}
        >
          <option value={128}>128x128 (Faster)</option>
          <option value={224}>224x224 (Standard)</option>
          <option value={299}>299x299 (Higher quality)</option>
        </select>
      </div>
      <div>
        <label className="block font-semibold mb-1">Batch Size</label>
        <select
          className="border w-full text-black p-1"
          value={modalParams.batch_size || 32}
          onChange={(e) => handleParamChange('batch_size', parseInt(e.target.value))}
        >
          <option value={16}>16 (Lower memory)</option>
          <option value={32}>32 (Recommended)</option>
          <option value={64}>64 (More memory needed)</option>
        </select>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block font-semibold mb-1">Epochs</label>
        <input
          type="number"
          min="1"
          max="50"
          className="border w-full text-black p-1"
          value={modalParams.epochs || 10}
          onChange={(e) => handleParamChange('epochs', parseInt(e.target.value))}
        />
        <p className="text-xs text-gray-500">Start with 10-15 for quick results</p>
      </div>
      <div>
        <label className="block font-semibold mb-1">Learning Rate</label>
        <select
          className="border w-full text-black p-1"
          value={modalParams.learning_rate || 0.001}
          onChange={(e) => handleParamChange('learning_rate', parseFloat(e.target.value))}
        >
          <option value={0.01}>0.01 (High)</option>
          <option value={0.001}>0.001 (Standard)</option>
          <option value={0.0001}>0.0001 (Low)</option>
        </select>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="flex items-center">
          <input
            type="checkbox"
            className="mr-2"
            checked={modalParams.fine_tune ?? true}
            onChange={(e) => handleParamChange('fine_tune', e.target.checked)}
          />
          <span className="text-sm">Enable Fine-tuning</span>
        </label>
        <p className="text-xs text-gray-500 mt-1">
          Improves accuracy by unfreezing some pre-trained layers
        </p>
      </div>
      <div>
        <label className="flex items-center">
          <input
            type="checkbox"
            className="mr-2"
            checked={modalParams.augmentation ?? true}
            onChange={(e) => handleParamChange('augmentation', e.target.checked)}
          />
          <span className="text-sm">Data Augmentation</span>
        </label>
        <p className="text-xs text-gray-500 mt-1">
          Applies rotations, flips to prevent overfitting
        </p>
      </div>
    </div>

    <div>
      <label className="block font-semibold mb-1">Test Split</label>
      <input
        type="number"
        step="0.05"
        min="0.1"
        max="0.5"
        className="border w-full text-black p-1"
        value={modalParams.test_size || 0.2}
        onChange={(e) => handleParamChange('test_size', parseFloat(e.target.value))}
      />
      <p className="text-xs text-gray-500 mt-1">
        Fraction of images used for validation (0.2 = 20%)
      </p>
    </div>
  </div>
)}

            {/* Save / Cancel buttons */}
            <div className="mt-4 flex justify-end space-x-4">
              <button
                onClick={handleCloseNodeConfigModal}
                className="px-3 py-1 rounded bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNodeConfig}
                className="px-3 py-1 rounded bg-blue-600 text-white"
              >
                Save Node
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Model Explanations Modal */}
<ModelExplanations
  explanations={currentExplanations}
  modelType={currentModelType}
  isOpen={showExplanationsModal}
  onClose={() => setShowExplanationsModal(false)}
/>

      {notification && (
        <NotificationModal
          {...notification}
          onClose={() => setNotification(null)}
        />
      )}

      {aiSuggestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex items-center gap-3">
              <div className="w-9 h-9 bg-white bg-opacity-20 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.347.347a3.913 3.913 0 01-1.136 2.754A3.913 3.913 0 0112 21a3.913 3.913 0 01-2.757-1.143 3.913 3.913 0 01-1.136-2.754l-.347-.347z" /></svg>
              </div>
              <div>
                <h3 className="text-white font-semibold text-base">Replace "Train Model" Node</h3>
                <p className="text-blue-100 text-xs">AI suggestion · powered by Gemini</p>
              </div>
            </div>

            <div className="px-6 py-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Suggested model</p>
                  <p className="text-xl font-bold text-gray-900 capitalize">{aiSuggestion.model_type?.replace(/_/g, ' ')}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 mb-1">Confidence</p>
                  <p className="text-xl font-bold text-blue-600">{Math.round((aiSuggestion.confidence || 0) * 100)}%</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl px-4 py-3 mb-5">
                <p className="text-xs font-medium text-gray-500 mb-1">Why this model?</p>
                <p className="text-sm text-gray-700 leading-relaxed">{aiSuggestion.reasoning}</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setAiSuggestion(null);
                    setLogs(prev => [
                      ...prev,
                      'Suggestion dismissed. Delete the "Train Model" node and add a specific model type from the panel.',
                    ]);
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  Do It Manually
                </button>
                <button
                  onClick={() => {
                    const targetNodeType = MODEL_TYPE_TO_NODE[aiSuggestion.model_type] || 'trainClassification';
                    const nodeDef = NODE_CATEGORIES.flatMap(c => c.nodes).find(n => n.type === targetNodeType);
                    const defaultParams = nodeDef?.defaultParams || {};
                    const oldNodeId = aiSuggestion.sourceNodeId;
                    const oldNode = nodes.find(n => n.id === oldNodeId);
                    const newNodeId = `${targetNodeType}-${Date.now()}`;

                    setNodes(nds => [
                      ...nds.filter(n => n.id !== oldNodeId),
                      {
                        id: newNodeId,
                        type: targetNodeType,
                        position: oldNode?.position || { x: 700, y: 100 },
                        data: {
                          label: nodeDef?.label || targetNodeType,
                          parameters: {
                            ...defaultParams,
                            model_type: aiSuggestion.model_type,
                            ...(aiSuggestion.parameters || {}),
                          },
                          status: 'pending',
                          results: null,
                          onDelete: handleDeleteNode,
                        },
                      },
                    ]);

                    setEdges(eds => eds.map(e => ({
                      ...e,
                      source: e.source === oldNodeId ? newNodeId : e.source,
                      target: e.target === oldNodeId ? newNodeId : e.target,
                    })));

                    setLogs(prev => [
                      ...prev,
                      `Node replaced with: "${aiSuggestion.model_type.replace(/_/g, ' ')}" (${Math.round((aiSuggestion.confidence || 0) * 100)}% confidence)`,
                      `Reason: ${aiSuggestion.reasoning}`,
                      'Click "Run Pipeline" again to execute.',
                    ]);
                    setAiSuggestion(null);
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all"
                >
                  Apply &amp; Replace Node
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
